<template>
  <div
    class="create-order-container"
    :class="{ 'is-basic-step': currentStep === 1, 'is-pos-step': currentStep === 2, 'is-checkout-step': currentStep === 3 }"
  >
    <div class="page-header page-header-order-create">
      <h1 class="page-title">新增訂單</h1>
      <div class="header-actions">
        <button ref="goToStep2BtnEl" v-if="currentStep === 1" type="button" class="btn btn-primary" @click="goToStep2">
          下一步：加入商品
          <i class="ri-arrow-right-line"></i>
        </button>
        <template v-if="currentStep === 2">
          <button type="button" class="btn btn-secondary" @click="goToStep1">
            <i class="ri-arrow-left-line"></i> 上一步
          </button>
          <button ref="goToStep3BtnEl" type="button" class="btn btn-primary" @click="goToStep3">
            下一步：結帳
            <i class="ri-arrow-right-line"></i>
          </button>
        </template>
        <template v-if="currentStep === 3">
          <button type="button" class="btn btn-secondary" @click="currentStep = 2">
            <i class="ri-arrow-left-line"></i> 上一步
          </button>
          <button ref="submitOrderBtnEl" type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
            <span v-if="submitting" class="spinner spinner-sm"></span>
            <span v-else><i class="ri-check-line"></i> 建立訂單</span>
          </button>
        </template>
        <button class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
      </div>
    </div>

    <div class="form-container">
      <!-- ===== STEP 1：基本資訊 ===== -->
      <div v-if="currentStep === 1">
        <div class="form-section">
          <h3 class="section-title">訂購人</h3>

          <!-- 訪客 / 會員 切換 -->
          <div class="customer-type-row">
            <button
              type="button"
              class="type-btn"
              :class="{ active: customerType === 'guest' }"
              @click="setCustomerType('guest')"
            >
              <i class="ri-user-line"></i> 訪客結帳
            </button>
            <button
              type="button"
              class="type-btn"
              :class="{ active: customerType === 'member' }"
              @click="setCustomerType('member')"
            >
              <i class="ri-vip-crown-line"></i> 會員
            </button>
          </div>

          <div v-if="customerType === 'member'" class="form-group mt-12">
            <MemberSearch
              ref="memberSearchRef"
              v-model="selectedOrderMembers"
              :max-selections="1"
              @selected="handleMemberSelected"
              @cleared="handleMemberCleared"
              placeholder="輸入手機號碼或姓名搜尋會員"
            />
            <small class="form-text">選擇會員後會顯示綠色確認區塊，結帳時可計算點數</small>
          </div>

          <div v-if="customerType === 'guest'" class="guest-notice">
            <i class="ri-information-line"></i>
            訪客結帳：不須輸入會員資料，點數不會累積
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">訂單設定</h3>

          <div class="form-group">
            <label>門市（選填）</label>
            <StoreSelector
              v-model="form.storeId"
              :stores="stores"
              :loading="storesLoading"
              :show-code="false"
              @change="saveLastStore"
            />
            <!-- 進銷存提示：store_warehouse 模式 -->
            <template v-if="storeWarehouseMode && form.storeId">
              <div v-if="selectedStoreWarehouseId" class="inventory-hint">
                <i class="ri-store-3-line"></i>
                訂單將從此門市對應倉庫扣除庫存
              </div>
              <div v-else class="inventory-alert">
                <i class="ri-alert-fill"></i>
                <div>
                  <strong>此門市尚未關聯倉庫，訂單將不扣除庫存！</strong>
                  <p>請至「門市管理 → 編輯門市」設定對應倉庫，否則庫存數量將不準確。</p>
                </div>
              </div>
            </template>
            <div v-else-if="inventoryEnabled && !storeWarehouseMode" class="inventory-hint">
              <i class="ri-archive-line"></i>
              訂單將依進銷存設定自動扣除庫存
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>訂單類型</label>
              <select v-model="form.orderType" class="form-control">
                <option value="pos">POS</option>
                <option value="ecom">電商</option>
                <option value="online_takeout">線上外帶</option>
                <option value="online_delivery">線上外送</option>
                <option value="online_dinein">線上內用</option>
              </select>
            </div>

            <div class="form-group">
              <label>訂單日期</label>
              <input v-model="form.orderDate" type="datetime-local" class="form-control" />
            </div>
          </div>

          <div class="form-group">
            <label>備註（選填）</label>
            <textarea
              v-model="form.remarks"
              class="form-control"
              rows="3"
              placeholder="請輸入備註"
              maxlength="500"
            ></textarea>
          </div>
        </div>

        <div class="step-actions">
          <button type="button" class="btn btn-secondary" @click="handleCancel">取消</button>
        </div>
      </div>

      <!-- ===== STEP 2：加入商品 ===== -->
      <div v-if="currentStep === 2" class="pos-screen" @click="handleStep2Interaction" @keydown.capture="handleStep2Keydown">
        <!-- 步驟 1 資訊摘要 -->
        <div class="step2-top-row">
          <div class="step1-summary compact">
            <div class="summary-items">
              <span class="summary-item">
                <i class="ri-user-line"></i>
                {{ selectedMember ? selectedMember.name || selectedMember.memberName : '訪客' }}
              </span>
              <span v-if="form.storeId" class="summary-item">
                <i class="ri-store-line"></i>
                {{ stores.find(s => s.id === form.storeId)?.storeName }}
              </span>
              <span class="summary-item">
                <i class="ri-shopping-bag-line"></i>
                {{ orderTypeLabel }}
              </span>
            </div>
          </div>
          <div class="step2-customer-inline-edit">
            <div class="customer-type-row compact">
              <button
                type="button"
                class="type-btn"
                :class="{ active: customerType === 'guest' }"
                @click="setCustomerType('guest')"
              >
                <i class="ri-user-line"></i> 訪客
              </button>
              <button
                type="button"
                class="type-btn"
                :class="{ active: customerType === 'member' }"
                @click="setCustomerType('member')"
              >
                <i class="ri-vip-crown-line"></i> 會員
              </button>
            </div>
            <div v-if="customerType === 'member'" class="step2-member-compact">
              <span class="step2-member-label">
                {{ selectedMember ? `會員：${selectedMember.name || selectedMember.memberName}` : '尚未選擇會員' }}
              </span>
              <button type="button" class="btn btn-ghost btn-sm" @click="toggleStep2MemberSearch">
                {{ showStep2MemberSearch ? '收起' : '選擇會員' }}
              </button>
            </div>
          </div>
        </div>
        <div v-if="customerType === 'member' && showStep2MemberSearch" class="step2-member-search">
            <MemberSearch
              ref="memberSearchRef"
              v-model="selectedOrderMembers"
              :max-selections="1"
              @selected="handleMemberSelected"
              @cleared="handleMemberCleared"
              placeholder="輸入手機號碼或姓名搜尋會員"
            />
        </div>

        <form @submit.prevent="handleSubmit">
          <div class="step2-layout">

            <!-- 左欄：訂單明細 -->
            <div class="step2-left">
              <div class="form-section">
                <h3 class="section-title">快速選品</h3>

                <div class="barcode-add-row">
                  <input
                    v-model="barcodeQuery"
                    type="text"
                    ref="barcodeInputEl"
                    class="form-control barcode-input"
                    placeholder="掃描條碼後按 Enter"
                    :disabled="barcodeLoading"
                    @keydown.enter.prevent="barcodeQuery.trim() ? handleBarcodeAdd() : openProductPicker()"
                    @keydown.tab="handleBarcodeTab"
                    @keydown.down="handleBarcodeTab"
                  />
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="!barcodeQuery.trim() || barcodeLoading"
                    @click="handleBarcodeAdd"
                  >
                    <i class="ri-barcode-line"></i>
                    {{ barcodeLoading ? '查詢中...' : '條碼' }}
                  </button>
                  <button type="button" class="btn btn-secondary btn-sm" @click="openProductPicker">
                    <i class="ri-list-check"></i> 挑選商品
                  </button>
                  <span v-if="barcodeError" class="barcode-error">{{ barcodeError }}</span>
                </div>

                <div class="quick-select-panel">
                  <div class="quick-select-header">
                    <h4>快速選商品</h4>
                    <input
                      v-model="quickSearchQuery"
                      type="text"
                      class="form-control quick-search-input"
                      placeholder="搜尋商品名稱/SKU"
                    />
                  </div>
                  <div class="quick-categories">
                    <button
                      type="button"
                      class="quick-cat-btn"
                      :class="{ active: selectedQuickCategoryId === 'all' }"
                      @click="selectedQuickCategoryId = 'all'"
                    >
                      全部
                    </button>
                    <button
                      v-for="cat in quickCategories"
                      :key="cat.id"
                      type="button"
                      class="quick-cat-btn"
                      :class="{ active: selectedQuickCategoryId === cat.id }"
                      @click="selectedQuickCategoryId = cat.id"
                    >
                      {{ cat.name }}
                    </button>
                  </div>

                  <div v-if="quickProductsLoading" class="quick-products-loading">載入商品中...</div>
                  <div v-else-if="quickProductsError" class="quick-products-error">
                    {{ quickProductsError }}
                    <button type="button" class="btn btn-ghost btn-sm" @click="fetchQuickProducts">
                      重試
                    </button>
                  </div>
                  <div v-else class="quick-products-grid">
                    <button
                      v-for="product in filteredQuickProducts"
                      :key="product.id"
                      type="button"
                      class="quick-product-card"
                      :disabled="quickAddLoadingByProductId[product.id]"
                      @click="quickAddProduct(product)"
                    >
                      <div class="quick-product-thumb-wrap">
                        <img
                          v-if="shouldShowQuickProductThumb(product)"
                          :src="getQuickProductThumb(product)"
                          class="quick-product-thumb"
                          alt=""
                          loading="lazy"
                          decoding="async"
                          @error="handleQuickProductThumbError(product.id)"
                        />
                        <div v-else class="quick-product-thumb quick-product-thumb--placeholder" aria-hidden="true">
                          <i class="ri-image-line"></i>
                        </div>
                      </div>
                      <div class="quick-product-name">{{ product.name }}</div>
                      <div class="quick-product-meta">
                        <span v-if="product.sku" class="quick-product-sku">SKU: {{ product.sku }}</span>
                        <span class="quick-product-price">{{ formatCurrency(product.unitPrice || 0) }}</span>
                      </div>
                    </button>
                    <div v-if="filteredQuickProducts.length === 0" class="quick-products-empty">
                      找不到符合條件的商品
                    </div>
                  </div>
                </div>

                <div class="quick-select-tip">
                  <i class="ri-hand-coin-line"></i>
                  點選商品後，已購買清單會顯示在右側
                </div>
              </div>
            </div>

            <!-- 右欄：金額與付款 -->
            <div class="step2-right">
              <!-- 金額摘要 -->
              <div class="amount-card">
                <div class="amount-card-header">
                  <h3 class="section-title">金額資訊</h3>
                  <button type="button" class="btn btn-ghost btn-sm" @click="showAmountDetails = !showAmountDetails">
                    {{ showAmountDetails ? '收合' : '展開' }}
                    <i :class="showAmountDetails ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                  </button>
                </div>

                <div v-if="!showAmountDetails" class="amount-collapsed">
                  <span class="amount-collapsed-label">應付金額</span>
                  <strong class="amount-collapsed-value">{{ formatCurrency(outstandingAmount) }}</strong>
                </div>

                <template v-else>
                  <div class="amount-row">
                    <span class="amount-label">商品小計</span>
                    <span class="amount-value">{{ formatCurrency(calculatedSubtotal) }}</span>
                  </div>
                  <div class="amount-row">
                    <span class="amount-label">整單折扣</span>
                    <div class="amount-input-wrap">
                      <input v-model.number="manualOrderDiscount" type="number" class="form-control amount-input" placeholder="0" min="0" step="0.01" @input="syncOrderDiscount" />
                    </div>
                  </div>
                  <div v-if="selectedCouponCode && couponDiscountAmount > 0" class="amount-row">
                    <span class="amount-label">優惠券折抵</span>
                    <span class="amount-value amount-value--discount">-{{ formatCurrency(couponDiscountAmount) }}</span>
                  </div>
                  <div class="amount-row">
                    <span class="amount-label">運費</span>
                    <div class="amount-input-wrap">
                      <input v-model.number="form.shippingFee" type="number" class="form-control amount-input" placeholder="0" min="0" step="0.01" @input="syncOrderDiscount" />
                    </div>
                  </div>
                  <div class="amount-divider"></div>
                  <div class="amount-row total-row">
                    <span class="amount-label">總金額</span>
                    <div class="amount-input-wrap">
                      <input
                        v-model.number="form.totalAmount"
                        type="number"
                        class="form-control amount-input total-input"
                        :class="{ 'is-invalid': errors.totalAmount }"
                        min="0" step="0.01" required
                        @input="calculatePaidAmount"
                      />
                    </div>
                  </div>
                  <div v-if="errors.totalAmount" class="invalid-feedback mb-8">{{ errors.totalAmount }}</div>
                  <div class="amount-row">
                    <span class="amount-label">已付金額</span>
                    <div class="amount-input-wrap">
                      <input
                        v-model.number="form.paidAmount"
                        type="number"
                        class="form-control amount-input"
                        :class="{ 'is-invalid': errors.paidAmount }"
                        min="0" step="0.01" required
                      />
                    </div>
                  </div>
                  <div v-if="errors.paidAmount" class="invalid-feedback mb-8">{{ errors.paidAmount }}</div>
                  <div class="paid-quick-actions">
                    <button type="button" class="btn btn-ghost btn-sm" @click="setPaidAmountByStatus('full')">
                      實收=總額
                    </button>
                    <button type="button" class="btn btn-ghost btn-sm" @click="setPaidAmountByStatus('zero')">
                      實收=0
                    </button>
                  </div>
                  <div class="change-row" :class="{ negative: paymentDiff < 0, positive: paymentDiff >= 0 }">
                    <span>{{ paymentDiff >= 0 ? '找零' : '應付金額' }}</span>
                    <strong>{{ formatCurrency(Math.abs(paymentDiff)) }}</strong>
                  </div>
                </template>

              </div>

              <div class="amount-card purchased-items-card mt-12">
                <div class="card-title-row">
                  <h3 class="section-title">已購買商品</h3>
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    :disabled="!hasOrderItems"
                    @click="clearAllItems"
                  >
                    <i class="ri-delete-bin-6-line"></i> 全部刪除
                  </button>
                </div>
                <div class="order-items">
                  <div v-for="(item, index) in form.items" :key="index" class="order-item">
                    <div class="item-main-row">
                      <div class="item-name-group">
                        <input
                          v-model="item.productName"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors[`items.${index}.productName`] }"
                          placeholder="商品名稱"
                          required
                        />
                        <input
                          v-model="item.productSku"
                          type="text"
                          class="form-control sku-input"
                          placeholder="SKU（選填）"
                        />
                      </div>
                      <div class="item-num-group">
                        <div class="item-num-cell">
                          <span class="num-label">數量</span>
                          <div class="qty-stepper">
                            <button type="button" class="qty-btn" @click="updateItemQuantity(index, -1)">
                              <i class="ri-subtract-line"></i>
                            </button>
                            <input
                              v-model.number="item.quantity"
                              type="number"
                              class="form-control qty-input"
                              :class="{ 'is-invalid': errors[`items.${index}.quantity`] }"
                              min="1"
                              required
                              @input="calculateItemSubtotal(index)"
                            />
                            <button type="button" class="qty-btn" @click="updateItemQuantity(index, 1)">
                              <i class="ri-add-line"></i>
                            </button>
                          </div>
                        </div>
                        <div class="item-num-cell">
                          <span class="num-label">單價</span>
                          <input
                            v-model.number="item.unitPrice"
                            type="number"
                            class="form-control"
                            :class="{ 'is-invalid': errors[`items.${index}.unitPrice`] }"
                            min="0"
                            step="0.01"
                            required
                            @input="calculateItemSubtotal(index)"
                          />
                        </div>
                        <div class="item-num-cell">
                          <span class="num-label">折扣</span>
                          <input
                            v-model.number="item.discountAmount"
                            type="number"
                            class="form-control"
                            min="0"
                            step="0.01"
                            @input="calculateItemSubtotal(index)"
                          />
                        </div>
                        <div class="item-num-cell subtotal-cell">
                          <span class="num-label">小計</span>
                          <span class="subtotal-value">{{ formatCurrency(item.subtotal) }}</span>
                        </div>
                        <button
                          type="button"
                          class="btn btn-ghost btn-sm item-delete-btn"
                          @click="removeItem(index)"
                        >
                          <i class="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-if="form.items.length === 0" class="order-items-empty">
                    尚未加入商品
                  </div>

                  <!-- 贈品券預覽（唯讀，不加入 form.items） -->
                  <div v-if="selectedCoupon && selectedCoupon.couponType === 'gift'" class="order-item gift-preview-item">
                    <div class="item-main-row">
                      <div class="item-name-group">
                        <div class="gift-item-name">
                          <span class="gift-item-badge"><i class="ri-gift-line"></i> 贈品</span>
                          {{ selectedCoupon.giftProductName || selectedCoupon.templateName || '贈品' }}
                        </div>
                        <div v-if="selectedCoupon.giftVariantName" class="gift-item-sku">{{ selectedCoupon.giftVariantName }}</div>
                      </div>
                      <div class="item-num-group">
                        <div class="item-num-cell">
                          <span class="num-label">數量</span>
                          <span class="gift-item-value">1</span>
                        </div>
                        <div class="item-num-cell">
                          <span class="num-label">單價</span>
                          <span class="gift-item-value">$0</span>
                        </div>
                        <div class="item-num-cell subtotal-cell">
                          <span class="num-label">小計</span>
                          <span class="subtotal-value gift-item-value">$0</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

          </div>
        </form>

        <!-- 共用商品挑選 Modal -->
        <ProductPickerModal
          :open="showProductPicker"
          title="挑選商品"
          @close="closeProductPicker"
          @select="onPickerSelect"
        />

        <div v-if="showQuickVariantPicker" class="quick-variant-overlay" @click.self="closeQuickVariantPicker">
          <div class="quick-variant-modal">
            <div class="quick-variant-header">
              <h4>選擇規格</h4>
              <button type="button" class="quick-variant-close" @click="closeQuickVariantPicker">
                <i class="ri-close-line"></i>
              </button>
            </div>
            <p class="quick-variant-product">{{ quickVariantProduct?.name }}</p>
            <div class="quick-variant-list">
              <button
                v-for="variant in quickVariantOptions"
                :key="variant.id"
                type="button"
                class="quick-variant-item"
                @click="handleQuickVariantSelect(variant)"
              >
                <span>{{ formatVariantLabel(variant) }}</span>
                <strong>{{ formatCurrency(variant.unitPrice ?? quickVariantProduct?.unitPrice ?? 0) }}</strong>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== STEP 3：結帳 ===== -->
      <div v-if="currentStep === 3" class="checkout-step" @keydown.enter.capture="handleCheckoutKeydown">
        <form @submit.prevent="handleSubmit" class="checkout-form">
          <div class="checkout-layout">
            <div class="checkout-left">
              <div class="amount-card">
                <h3 class="section-title">結帳商品明細</h3>
                <div class="checkout-items-list">
                  <div v-for="(item, index) in form.items" :key="`checkout-${index}`" class="checkout-item-row">
                    <div class="checkout-item-thumb-wrap">
                      <img
                        v-if="shouldShowCheckoutItemThumb(item, index)"
                        :src="getCheckoutItemThumb(item)"
                        class="checkout-item-thumb"
                        alt=""
                        loading="lazy"
                        decoding="async"
                        @error="handleCheckoutItemThumbError(index)"
                      />
                      <div v-else class="checkout-item-thumb checkout-item-thumb--placeholder" aria-hidden="true">
                        <i class="ri-image-line"></i>
                      </div>
                    </div>
                    <div class="checkout-item-main">
                      <div class="checkout-item-name">{{ item.productName || '未命名商品' }}</div>
                      <div class="checkout-item-meta" v-if="item.productSku">SKU: {{ item.productSku }}</div>
                    </div>
                    <div class="checkout-item-calc">
                      <span>{{ item.quantity }} x {{ formatCurrency(item.unitPrice || 0) }}</span>
                      <span v-if="item.discountAmount > 0" class="checkout-item-discount">-{{ formatCurrency(item.discountAmount) }}</span>
                      <strong>{{ formatCurrency(item.subtotal || 0) }}</strong>
                    </div>
                  </div>

                  <!-- 贈品券預覽（唯讀） -->
                  <div v-if="selectedCoupon && selectedCoupon.couponType === 'gift'" class="checkout-item-row checkout-gift-row">
                    <div class="checkout-item-thumb-wrap">
                      <div class="checkout-item-thumb checkout-item-thumb--gift" aria-hidden="true">
                        <i class="ri-gift-line"></i>
                      </div>
                    </div>
                    <div class="checkout-item-main">
                      <div class="checkout-item-name">
                        <span class="gift-item-badge"><i class="ri-gift-line"></i> 贈品</span>
                        {{ selectedCoupon.giftProductName || selectedCoupon.templateName || '贈品' }}
                      </div>
                      <div v-if="selectedCoupon.giftVariantName" class="checkout-item-meta">{{ selectedCoupon.giftVariantName }}</div>
                    </div>
                    <div class="checkout-item-calc">
                      <span>1 x $0</span>
                      <strong>$0</strong>
                    </div>
                  </div>

                  <div v-if="form.items.length === 0" class="order-items-empty">
                    尚未加入商品
                  </div>
                </div>
              </div>
            </div>

            <div class="checkout-right">
              <div class="amount-card checkout-amount-summary">
                <h3 class="section-title">金額摘要</h3>
                <div class="amount-row">
                  <span class="amount-label">商品小計</span>
                  <span class="amount-value">{{ formatCurrency(calculatedSubtotal) }}</span>
                </div>
                <div class="amount-row">
                  <span class="amount-label">整單折扣</span>
                  <div class="amount-input-wrap">
                    <input
                      v-model.number="manualOrderDiscount"
                      type="number"
                      class="form-control amount-input"
                      placeholder="0"
                      min="0"
                      step="0.01"
                      @input="syncOrderDiscount"
                    />
                  </div>
                </div>
                <div v-if="selectedCouponCode && couponDiscountAmount > 0" class="amount-row">
                  <span class="amount-label">優惠券折抵</span>
                  <span class="amount-value amount-value--discount">-{{ formatCurrency(couponDiscountAmount) }}</span>
                </div>
                <div v-if="selectedCouponCode && couponFreeShipping" class="amount-row">
                  <span class="amount-label">運費優惠</span>
                  <span class="amount-value amount-value--discount">免運</span>
                </div>
                <div v-if="selectedCouponCode && selectedCoupon?.couponType === 'gift'" class="amount-row">
                  <span class="amount-label">贈品券</span>
                  <span class="amount-value">
                    {{ selectedCoupon.giftProductName || selectedCoupon.templateName || '結帳後核銷' }}
                    <span v-if="selectedCoupon.giftProductName" class="gift-badge">贈品 x1</span>
                  </span>
                </div>

                <div v-if="form.memberId" class="checkout-coupon-row">
                  <button
                    type="button"
                    class="checkout-coupon-trigger"
                    :class="{
                      'is-selected': !!selectedCoupon,
                      'is-available': !selectedCoupon && checkoutCouponOptions.length && !couponsLoading,
                      'is-empty': !checkoutCouponOptions.length && !couponsLoading && !couponsError,
                    }"
                    :disabled="couponsLoading"
                    @click="openCouponPicker"
                  >
                    <span class="checkout-coupon-trigger-label">
                      <i class="ri-coupon-3-line"></i>
                      優惠券
                    </span>
                    <span class="checkout-coupon-trigger-text">
                      <template v-if="couponsLoading">載入中...</template>
                      <template v-else-if="couponsError">{{ couponsError }}</template>
                      <template v-else-if="selectedCoupon">
                        {{ selectedCoupon.templateName || selectedCoupon.couponCode }}
                        <span class="checkout-coupon-trigger-sub">
                          · {{ formatCouponBenefit(selectedCoupon) }}
                        </span>
                      </template>
                      <template v-else-if="checkoutCouponOptions.length">
                        選擇優惠券（{{ eligibleCouponCount }} 張可用）
                      </template>
                      <template v-else>尚無可用</template>
                    </span>
                    <i v-if="!couponsLoading && checkoutCouponOptions.length" class="ri-arrow-right-s-line checkout-coupon-arrow"></i>
                  </button>
                </div>
                <div v-if="form.memberId && selectedCouponCode" class="checkout-coupon-clear-row">
                  <button
                    type="button"
                    class="btn-link btn-link-sm"
                    @click="clearSelectedCoupon"
                  >
                    清除優惠券
                  </button>
                </div>
                <div class="amount-row">
                  <span class="amount-label">運費</span>
                  <span class="amount-value">
                    <template v-if="couponFreeShipping">$0</template>
                    <template v-else>{{ formatCurrency(form.shippingFee || 0) }}</template>
                  </span>
                </div>
                <div class="amount-divider"></div>
                <div class="amount-row total-row checkout-total-row">
                  <span class="amount-label">總金額</span>
                  <span class="amount-value checkout-total-value">{{ formatCurrency(form.totalAmount || 0) }}</span>
                </div>
                <div class="amount-row">
                  <span class="amount-label">已付金額</span>
                  <span class="amount-value">{{ formatCurrency(form.paidAmount || 0) }}</span>
                </div>
                <div class="change-row" :class="{ negative: paymentDiff < 0, positive: paymentDiff >= 0 }">
                  <span>{{ paymentDiff >= 0 ? '找零' : '應付金額' }}</span>
                  <strong>{{ formatCurrency(Math.abs(paymentDiff)) }}</strong>
                </div>
                <div class="amount-divider"></div>
                <div class="points-estimate-row">
                  <span class="amount-label">預估可得點數</span>
                  <div class="points-estimate-value-wrap">
                    <span v-if="pointsEstimateLoading" class="points-estimate-loading">試算中...</span>
                    <template v-else>
                      <strong
                        class="points-estimate-value"
                        :class="{ 'is-zero': !pointsEstimate?.eligible }"
                      >
                        {{ formatPointsEstimate(pointsEstimate?.estimatedPoints) }}
                      </strong>
                      <small v-if="pointsEstimate?.message" class="points-estimate-hint">
                        {{ pointsEstimate.message }}
                      </small>
                    </template>
                  </div>
                </div>
              </div>

              <div class="amount-card mt-12">
                <h3 class="section-title">付款方式</h3>
                <div class="payment-method-buttons" :class="{ 'is-invalid': errors.paymentMethod }">
                  <button
                    v-for="pm in paymentMethods"
                    :key="pm.value"
                    :ref="(el) => setCheckoutPaymentMethodBtnRef(pm.value, el)"
                    type="button"
                    class="payment-btn"
                    :class="{ active: form.paymentMethod === pm.value }"
                    :data-value="pm.value"
                    @click="selectPaymentMethod(pm.value)"
                  >
                    <i :class="pm.icon"></i>
                    {{ pm.label }}
                  </button>
                </div>
                <div v-if="errors.paymentMethod" class="invalid-feedback mt-6">{{ errors.paymentMethod }}</div>

                <h3 class="section-title payment-status-title">付款狀態</h3>
                <div class="payment-method-buttons" :class="{ 'is-invalid': errors.paymentStatus }">
                  <button
                    v-for="ps in paymentStatuses"
                    :key="ps.value"
                    :ref="(el) => setCheckoutPaymentStatusBtnRef(ps.value, el)"
                    type="button"
                    class="payment-btn"
                    :class="{ active: form.paymentStatus === ps.value }"
                    :data-value="ps.value"
                    @click="selectPaymentStatus(ps.value)"
                  >
                    <i :class="ps.icon"></i>
                    {{ ps.label }}
                  </button>
                </div>
                <div v-if="errors.paymentStatus" class="invalid-feedback mt-6">{{ errors.paymentStatus }}</div>
              </div>
            </div>
          </div>
        </form>

        <div
          v-if="showCouponUsePrompt"
          class="coupon-prompt-overlay"
          @click.self="closeCouponUsePrompt"
          @keydown.capture="handleCouponPromptKeydown"
        >
          <div class="coupon-prompt-modal" role="dialog" aria-modal="true" aria-labelledby="coupon-use-prompt-title">
            <div class="coupon-prompt-header">
              <h4 id="coupon-use-prompt-title">是否使用優惠券？</h4>
            </div>
            <p class="coupon-prompt-text">
              此會員有 {{ eligibleCouponCount }} 張可使用的優惠券，但目前尚未選擇。是否要使用優惠券？
            </p>
            <div class="coupon-prompt-actions">
              <button
                ref="couponPromptNoBtnEl"
                type="button"
                class="btn btn-secondary coupon-prompt-btn"
                @click="declineCouponUseAndSubmit"
              >
                否，不使用優惠券
              </button>
              <button
                ref="couponPromptYesBtnEl"
                type="button"
                class="btn btn-primary coupon-prompt-btn"
                @click="acceptCouponUse"
              >
                是，選擇優惠券
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="showCouponPicker"
          class="coupon-picker-overlay"
          @click.self="closeCouponPicker"
          @keydown.capture="handleCouponPickerKeydown"
        >
          <div
            ref="couponPickerModalEl"
            class="coupon-picker-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="coupon-picker-title"
          >
            <div class="coupon-picker-header">
              <h4 id="coupon-picker-title">選擇優惠券</h4>
              <button type="button" class="coupon-picker-close" @click="closeCouponPicker">
                <i class="ri-close-line"></i>
              </button>
            </div>
            <p v-if="couponsLoading" class="checkout-coupon-status">載入優惠券中...</p>
            <p v-else-if="couponsError" class="checkout-coupon-status is-error">{{ couponsError }}</p>
            <p v-else-if="!checkoutCouponOptions.length" class="checkout-coupon-status">此會員尚無可用優惠券</p>
            <ul v-else class="checkout-coupon-list">
              <li
                v-for="coupon in checkoutCouponOptions"
                :key="coupon.couponCode"
                class="checkout-coupon-item"
                :class="{
                  'is-disabled': !coupon.eligible,
                  'is-selected': selectedCouponCode === coupon.couponCode,
                }"
              >
                <button
                  type="button"
                  class="checkout-coupon-label"
                  :data-coupon-code="coupon.couponCode"
                  :disabled="!coupon.eligible"
                  @click="selectCouponAndClose(coupon.couponCode)"
                >
                  <span class="checkout-coupon-radio" aria-hidden="true"></span>
                  <span class="checkout-coupon-body">
                    <span class="checkout-coupon-title">
                      {{ coupon.templateName || coupon.couponCode }}
                    </span>
                    <span class="checkout-coupon-meta">
                      <span class="checkout-coupon-type">{{ getCouponTypeText(coupon.couponType) }}</span>
                      <span class="checkout-coupon-benefit">{{ formatCouponBenefit(coupon) }}</span>
                      <span v-if="coupon.previewDiscount > 0" class="checkout-coupon-preview">
                        試算 -{{ formatCurrency(coupon.previewDiscount) }}
                      </span>
                      <span v-else-if="coupon.previewFreeShipping" class="checkout-coupon-preview">試算免運</span>
                    </span>
                    <span v-if="coupon.ineligibleReason" class="checkout-coupon-ineligible">
                      {{ coupon.ineligibleReason }}
                    </span>
                  </span>
                </button>
              </li>
            </ul>
            <div class="coupon-picker-footer">
              <button
                v-if="selectedCouponCode"
                type="button"
                class="btn btn-ghost btn-sm coupon-picker-footer-btn"
                @click="clearSelectedCouponAndClose"
              >
                清除選擇
              </button>
              <button type="button" class="btn btn-secondary btn-sm coupon-picker-footer-btn" @click="closeCouponPicker">
                關閉
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="showOrderConfirm"
          class="order-confirm-overlay"
          @click.self="closeOrderConfirm"
          @keydown.capture="handleOrderConfirmKeydown"
        >
          <div class="order-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="order-confirm-title">
            <div class="order-confirm-header">
              <h4 id="order-confirm-title">確認建立訂單</h4>
              <p class="order-confirm-subtitle">請再次確認以下資訊，確認後才會建立訂單。</p>
            </div>

            <div class="order-confirm-due">
              <span class="order-confirm-due-label">應付金額</span>
              <strong class="order-confirm-due-value">{{ formatCurrency(form.totalAmount || 0) }}</strong>
            </div>

            <div class="order-confirm-body">
              <section class="order-confirm-section">
                <h5 class="order-confirm-section-title">商品明細</h5>
                <ul class="order-confirm-items">
                  <li
                    v-for="(item, index) in form.items"
                    :key="`confirm-item-${index}`"
                    class="order-confirm-item"
                  >
                    <div class="order-confirm-item-main">
                      <span class="order-confirm-item-name">{{ item.productName || '未命名商品' }}</span>
                      <span v-if="item.productSku" class="order-confirm-item-meta">SKU: {{ item.productSku }}</span>
                    </div>
                    <div class="order-confirm-item-calc">
                      <span>{{ item.quantity }} x {{ formatCurrency(item.unitPrice || 0) }}</span>
                      <strong>{{ formatCurrency(item.subtotal || 0) }}</strong>
                    </div>
                  </li>
                  <li
                    v-if="selectedCoupon && selectedCoupon.couponType === 'gift'"
                    class="order-confirm-item order-confirm-item--gift"
                  >
                    <div class="order-confirm-item-main">
                      <span class="order-confirm-item-name">
                        <i class="ri-gift-line"></i>
                        {{ selectedCoupon.giftProductName || selectedCoupon.templateName || '贈品' }}
                      </span>
                      <span class="order-confirm-item-meta">贈品券兌換</span>
                    </div>
                    <div class="order-confirm-item-calc">
                      <span>1 x $0</span>
                      <strong>$0</strong>
                    </div>
                  </li>
                </ul>
              </section>

              <section class="order-confirm-section">
                <h5 class="order-confirm-section-title">票券</h5>
                <div v-if="selectedCoupon" class="order-confirm-coupon">
                  <span class="order-confirm-coupon-name">
                    {{ selectedCoupon.templateName || selectedCoupon.couponCode }}
                  </span>
                  <span class="order-confirm-coupon-benefit">{{ formatCouponBenefit(selectedCoupon) }}</span>
                  <span v-if="couponDiscountAmount > 0" class="order-confirm-coupon-discount">
                    折抵 {{ formatCurrency(couponDiscountAmount) }}
                  </span>
                  <span v-else-if="couponFreeShipping" class="order-confirm-coupon-discount">免運</span>
                </div>
                <p v-else class="order-confirm-empty">未使用優惠券</p>
              </section>

              <section class="order-confirm-section">
                <h5 class="order-confirm-section-title">點數</h5>
                <div v-if="form.memberId" class="order-confirm-points">
                  <span v-if="pointsEstimateLoading">試算中...</span>
                  <template v-else>
                    <strong :class="{ 'is-zero': !pointsEstimate?.eligible }">
                      {{ formatPointsEstimate(pointsEstimate?.estimatedPoints) }}
                    </strong>
                    <small v-if="pointsEstimate?.message">{{ pointsEstimate.message }}</small>
                  </template>
                </div>
                <p v-else class="order-confirm-empty">訪客結帳，不累積點數</p>
              </section>
            </div>

            <div class="order-confirm-actions">
              <button
                ref="orderConfirmCancelBtnEl"
                type="button"
                class="btn btn-secondary order-confirm-action-btn"
                :disabled="submitting"
                @click="closeOrderConfirm"
              >
                返回修改
              </button>
              <button
                ref="orderConfirmSubmitBtnEl"
                type="button"
                class="btn btn-primary order-confirm-action-btn"
                :disabled="submitting"
                @click="confirmOrderSubmit"
              >
                <span v-if="submitting" class="spinner spinner-sm"></span>
                <span v-else><i class="ri-check-line"></i> 確認建立訂單</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { orderService } from '@/api/modules/orders.js';
import { couponService } from '@/api/modules/coupons.js';
import { storeService } from '@/api/modules/stores.js';
import { productService } from '@/api/modules/products.js';
import { clientSettingsService } from '@/api/modules/clientSettings.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import MemberSearch from '@/components/MemberSearch.vue';
import ProductPickerModal from '@/components/ProductPickerModal.vue';
import StoreSelector from '@/components/StoreSelector.vue';
import { showSuccess, showError } from '@/utils/swal.js';
import Swal from 'sweetalert2';

export default {
  name: 'OrderCreate',
  components: { MemberSearch, ProductPickerModal, StoreSelector },
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('新增訂單');

    // 步驟控制
    const currentStep = ref(1);
    const customerType = ref('guest'); // 'guest' | 'member'

    const submitting = ref(false);
    const selectedOrderMembers = ref([]);
    const selectedMember = ref(null);
    const stores = ref([]);
    const storesLoading = ref(false);
    const inventorySettings = ref(null);
    const inventoryEnabled = computed(() => {
      const mode = inventorySettings.value?.order_deduct_mode;
      return mode && mode !== 'disabled';
    });
    const storeWarehouseMode = computed(() => inventorySettings.value?.order_deduct_mode === 'store_warehouse');
    const selectedStoreWarehouseId = computed(() => {
      if (!storeWarehouseMode.value || !form.storeId) return null;
      const store = stores.value.find(s => s.id === form.storeId);
      return store?.warehouseId || null;
    });
    const barcodeQuery = ref('');
    const barcodeLoading = ref(false);
    const barcodeError = ref('');
    const quickProducts = ref([]);
    const quickProductsLoading = ref(false);
    const quickProductsError = ref('');
    const quickProductThumbErrorById = ref({});
    const checkoutItemThumbErrorByIndex = ref({});
    const pointsEstimate = ref(null);
    const pointsEstimateLoading = ref(false);
    let pointsEstimateTimer = null;
    const manualOrderDiscount = ref(0);
    const selectedCouponCode = ref(null);
    const availableCoupons = ref([]);
    const couponsLoading = ref(false);
    const couponsError = ref('');
    const quickCategories = ref([]);
    const selectedQuickCategoryId = ref('all');
    const quickSearchQuery = ref('');
    const quickAddLoadingByProductId = reactive({});
    const showQuickVariantPicker = ref(false);
    const quickVariantProduct = ref(null);
    const quickVariantOptions = ref([]);
    const showAmountDetails = ref(false);
    const showStep2MemberSearch = ref(false);
    const couponPickerModalEl = ref(null);
    const showCouponPicker = ref(false);
    const showCouponUsePrompt = ref(false);
    const showOrderConfirm = ref(false);
    const pendingSubmitAfterCoupon = ref(false);

    // 商品挑選 Modal
    const showProductPicker = ref(false);
    const memberSearchRef = ref(null);
    const barcodeInputEl = ref(null);
    const goToStep2BtnEl = ref(null);
    const goToStep3BtnEl = ref(null);
    const submitOrderBtnEl = ref(null);
    const couponPromptNoBtnEl = ref(null);
    const couponPromptYesBtnEl = ref(null);
    const orderConfirmCancelBtnEl = ref(null);
    const orderConfirmSubmitBtnEl = ref(null);
    const checkoutPaymentMethodBtnRefs = ref({});
    const checkoutPaymentStatusBtnRefs = ref({});
    /** 結帳 Enter 流程：payment_method → payment_status → submit */
    const checkoutFocusStage = ref('payment_method');

    const form = reactive({
      memberId: null,
      storeId: localStorage.getItem('order_last_store_id') || '',
      orderType: 'pos',
      orderSource: 'manual',
      subtotal: 0,
      discountAmount: 0,
      shippingFee: 0,
      taxAmount: 0,
      totalAmount: 0,
      paidAmount: 0,
      paymentMethod: '',
      paymentStatus: '',
      status: 'completed',
      orderDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16),
      remarks: '',
      items: [
        { productName: '', productSku: '', quantity: 1, unitPrice: 0, discountAmount: 0, subtotal: 0 }
      ]
    });

    const errors = reactive({});

    const orderTypeLabel = computed(() => {
      const map = { pos: 'POS', ecom: '電商', online_takeout: '線上外帶', online_delivery: '線上外送', online_dinein: '線上內用' };
      return map[form.orderType] || form.orderType;
    });

    const paymentMethods = [
      { value: 'cash',           label: '現金',     icon: 'ri-money-dollar-circle-line' },
      { value: 'credit_card',    label: '信用卡',   icon: 'ri-bank-card-line' },
      { value: 'debit_card',     label: '金融卡',   icon: 'ri-secure-payment-line' },
      { value: 'mobile_payment', label: '行動支付', icon: 'ri-smartphone-line' },
      { value: 'transfer',       label: '轉帳',     icon: 'ri-exchange-dollar-line' },
      { value: 'other',          label: '其他',     icon: 'ri-more-line' },
    ];

    const paymentStatuses = [
      { value: 'paid',     label: '已付款',   icon: 'ri-checkbox-circle-line' },
      { value: 'pending',  label: '待付款',   icon: 'ri-time-line' },
      { value: 'failed',   label: '付款失敗', icon: 'ri-close-circle-line' },
      { value: 'refunded', label: '已退款',   icon: 'ri-refund-2-line' },
    ];

    const paymentMethodValues = new Set(paymentMethods.map((m) => m.value));
    const paymentStatusValues = new Set(paymentStatuses.map((s) => s.value));

    const calculatedSubtotal = computed(() =>
      form.items.reduce((sum, item) => {
        const s = (item.quantity || 0) * (item.unitPrice || 0) - (item.discountAmount || 0);
        return sum + Math.max(0, s);
      }, 0)
    );

    const filteredQuickProducts = computed(() => {
      const kw = quickSearchQuery.value.trim().toLowerCase();
      return quickProducts.value.filter((product) => {
        const selectedCategory = quickCategories.value.find(cat => cat.id === selectedQuickCategoryId.value);
        const productCategoryId = product.categoryId || product.productCategoryId || product.category?.id || null;
        const productCategoryName = product.categoryName || product.category?.name || '';
        const categoryMatched = selectedQuickCategoryId.value === 'all'
          || productCategoryId === selectedQuickCategoryId.value
          || (selectedCategory && productCategoryName && selectedCategory.name === productCategoryName);
        if (!categoryMatched) return false;
        if (!kw) return true;
        const name = String(product.name || '').toLowerCase();
        const sku = String(product.sku || '').toLowerCase();
        return name.includes(kw) || sku.includes(kw);
      });
    });

    const hasOrderItems = computed(() =>
      form.items.some(item => (item.productName || '').trim() !== '' || Number(item.unitPrice || 0) > 0)
    );

    const extractProductImageUrl = (source) => {
      if (!source || typeof source !== 'object') return '';
      const url = source.imageUrl || source.mainImageUrl || source.main_image_url || source.image_url || '';
      return typeof url === 'string' ? url.trim() : '';
    };

    const getQuickProductThumb = (product) => extractProductImageUrl(product);

    const getCheckoutItemThumb = (item) => {
      const direct = extractProductImageUrl(item);
      if (direct) return direct;
      if (item?.productId) {
        const product = quickProducts.value.find(p => p.id === item.productId);
        return getQuickProductThumb(product);
      }
      return '';
    };

    const shouldShowCheckoutItemThumb = (item, index) => {
      const url = getCheckoutItemThumb(item);
      return Boolean(url) && !checkoutItemThumbErrorByIndex.value[index];
    };

    const handleCheckoutItemThumbError = (index) => {
      checkoutItemThumbErrorByIndex.value = {
        ...checkoutItemThumbErrorByIndex.value,
        [index]: true,
      };
    };

    const shouldShowQuickProductThumb = (product) => {
      const productId = product?.id;
      if (!productId) return false;
      const thumb = getQuickProductThumb(product);
      return Boolean(thumb) && !quickProductThumbErrorById.value[productId];
    };

    const handleQuickProductThumbError = (productId) => {
      if (!productId) return;
      quickProductThumbErrorById.value = {
        ...quickProductThumbErrorById.value,
        [productId]: true,
      };
    };

    const calcCouponMonetaryDiscount = (coupon, amountEligible) => {
      if (!coupon) return { discount: 0, freeShipping: false };
      const eligible = Number(amountEligible || 0);
      const min = Number(coupon.minPurchaseAmount || 0);
      if (eligible < min) return { discount: 0, freeShipping: false };

      const type = coupon.couponType;
      const val = Number(coupon.discountValue || 0);
      const maxD = coupon.maxDiscountAmount != null && coupon.maxDiscountAmount !== ''
        ? Number(coupon.maxDiscountAmount)
        : null;

      if (type === 'free_shipping') {
        return { discount: 0, freeShipping: true };
      }
      if (type === 'fixed_amount') {
        return { discount: Math.min(val, eligible), freeShipping: false };
      }
      if (type === 'discount' && coupon.discountUnit === 'percent') {
        let d = eligible * (val / 100);
        if (maxD != null && !Number.isNaN(maxD)) d = Math.min(d, maxD);
        return { discount: Math.min(d, eligible), freeShipping: false };
      }
      if (type === 'discount' && coupon.discountUnit === 'amount') {
        return { discount: Math.min(val, eligible), freeShipping: false };
      }
      return { discount: 0, freeShipping: false };
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券',
      };
      return typeMap[type] || type;
    };

    const formatCouponBenefit = (coupon) => {
      if (!coupon) return '';
      const val = Number(coupon.discountValue || 0);
      const money = `$${val.toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
      if (coupon.couponType === 'discount' && coupon.discountUnit === 'percent') {
        return `${val}% 折扣`;
      }
      if (coupon.couponType === 'discount' && coupon.discountUnit === 'amount') {
        return `折抵 ${money}`;
      }
      if (coupon.couponType === 'fixed_amount') {
        return `折價 ${money}`;
      }
      if (coupon.couponType === 'free_shipping') {
        return '免運';
      }
      if (coupon.couponType === 'gift') {
        if (coupon.giftProductName) return `贈品：${coupon.giftProductName}`;
        return '贈品兌換';
      }
      return getCouponTypeText(coupon.couponType);
    };

    const selectedCoupon = computed(() => {
      if (!selectedCouponCode.value) return null;
      return availableCoupons.value.find(c => c.couponCode === selectedCouponCode.value) || null;
    });

    const couponDiscountPreview = computed(() => {
      const coupon = selectedCoupon.value;
      if (!coupon) return { discount: 0, freeShipping: false };
      return calcCouponMonetaryDiscount(coupon, calculatedSubtotal.value);
    });

    const couponDiscountAmount = computed(() => couponDiscountPreview.value.discount);
    const couponFreeShipping = computed(() => couponDiscountPreview.value.freeShipping);

    const checkoutCouponOptions = computed(() => {
      const subtotal = calculatedSubtotal.value;
      const posCouponTypes = ['discount', 'fixed_amount', 'gift', 'free_shipping'];
      return availableCoupons.value
        .filter(coupon => posCouponTypes.includes(coupon.couponType))
        .map((coupon) => {
          const min = Number(coupon.minPurchaseAmount || 0);
          const eligible = subtotal >= min;
          const preview = calcCouponMonetaryDiscount(coupon, subtotal);
          let ineligibleReason = '';
          if (!eligible) {
            ineligibleReason = `需滿 $${min.toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
          }
          return {
            ...coupon,
            eligible,
            ineligibleReason,
            previewDiscount: preview.discount,
            previewFreeShipping: preview.freeShipping,
          };
        })
        .sort((a, b) => {
          if (a.eligible === b.eligible) return 0;
          return a.eligible ? -1 : 1;
        });
    });

    const eligibleCouponCount = computed(() =>
      checkoutCouponOptions.value.filter(coupon => coupon.eligible).length
    );

    const syncOrderDiscount = () => {
      form.discountAmount = (Number(manualOrderDiscount.value) || 0) + (couponDiscountAmount.value || 0);
      calculateTotal();
    };

    const calculateTotal = () => {
      form.subtotal = calculatedSubtotal.value;
      const shippingFee = couponFreeShipping.value ? 0 : (form.shippingFee || 0);
      form.totalAmount = form.subtotal - (form.discountAmount || 0) + shippingFee + (form.taxAmount || 0);
      calculatePaidAmount();
    };

    const clearSelectedCoupon = () => {
      selectedCouponCode.value = null;
      syncOrderDiscount();
      fetchPointsEstimate();
    };

    const openCouponPicker = async () => {
      if (couponsLoading.value || !checkoutCouponOptions.value.length) return;
      showCouponPicker.value = true;
      await focusCouponPickerOption();
    };

    const getEligibleCouponButtons = () => {
      if (!couponPickerModalEl.value) return [];
      return Array.from(couponPickerModalEl.value.querySelectorAll('.checkout-coupon-label:not(:disabled)'));
    };

    const getCouponPickerFooterButtons = () => {
      if (!couponPickerModalEl.value) return [];
      return Array.from(couponPickerModalEl.value.querySelectorAll('.coupon-picker-footer-btn:not(:disabled)'));
    };

    const getCouponPickerFocusables = () => [
      ...getEligibleCouponButtons(),
      ...getCouponPickerFooterButtons(),
    ];

    const focusCouponPickerAt = async (index) => {
      await nextTick();
      const focusables = getCouponPickerFocusables();
      if (!focusables.length) return;
      const safeIndex = ((index % focusables.length) + focusables.length) % focusables.length;
      const target = focusables[safeIndex];
      target.focus();
      target.scrollIntoView({ block: 'nearest' });
    };

    const focusCouponPickerOption = async () => {
      await nextTick();
      const couponButtons = getEligibleCouponButtons();
      if (!couponButtons.length) {
        getCouponPickerFooterButtons()[0]?.focus();
        return;
      }
      let target = couponButtons[0];
      if (selectedCouponCode.value) {
        const selected = couponButtons.find(
          (btn) => btn.dataset.couponCode === selectedCouponCode.value
        );
        if (selected) target = selected;
      }
      target.focus();
      target.scrollIntoView({ block: 'nearest' });
    };

    const handleCouponPickerKeydown = (e) => {
      if (!showCouponPicker.value) return;
      if (couponsLoading.value || !checkoutCouponOptions.value.length) return;

      const focusables = getCouponPickerFocusables();
      if (!focusables.length) return;

      const activeEl = document.activeElement;
      const currentIdx = focusables.indexOf(activeEl);

      if (e.key === 'Enter') {
        const couponCode = activeEl?.dataset?.couponCode;
        if (couponCode) {
          e.preventDefault();
          selectCouponAndClose(couponCode);
        }
        return;
      }

      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      e.preventDefault();

      if (currentIdx < 0) {
        focusCouponPickerOption();
        return;
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        focusCouponPickerAt(currentIdx + 1);
        return;
      }
      focusCouponPickerAt(currentIdx - 1);
    };

    const closeCouponPicker = () => {
      showCouponPicker.value = false;
      pendingSubmitAfterCoupon.value = false;
    };

    const selectCouponAndClose = async (couponCode) => {
      selectedCouponCode.value = couponCode;
      syncOrderDiscount();
      fetchPointsEstimate();
      const shouldFocusSubmit = pendingSubmitAfterCoupon.value;
      showCouponPicker.value = false;
      pendingSubmitAfterCoupon.value = false;
      if (shouldFocusSubmit) {
        checkoutFocusStage.value = 'submit';
        await nextTick();
        submitOrderBtnEl.value?.focus();
      }
    };

    const clearSelectedCouponAndClose = () => {
      clearSelectedCoupon();
      closeCouponPicker();
    };

    const loadMemberCoupons = async () => {
      if (!form.memberId) {
        availableCoupons.value = [];
        selectedCouponCode.value = null;
        syncOrderDiscount();
        return;
      }
      couponsLoading.value = true;
      couponsError.value = '';
      try {
        const response = await couponService.getAvailableCoupons(form.memberId, {
          page: 1,
          pageSize: 100,
        });
        const payload = response?.data ?? response;
        availableCoupons.value = Array.isArray(payload?.items)
          ? payload.items
          : (Array.isArray(payload) ? payload : []);
      } catch (err) {
        console.error('載入優惠券失敗:', err);
        couponsError.value = err.response?.data?.message || err.message || '載入優惠券失敗';
        availableCoupons.value = [];
      } finally {
        couponsLoading.value = false;
      }
    };

    const calculatePaidAmount = () => {
      if (form.paymentStatus === 'paid') {
        // 已付款時實付金額應跟隨訂單總額（含折價券、運費變更）
        form.paidAmount = form.totalAmount ?? 0;
      } else if (form.paymentStatus === 'pending' && form.paidAmount > form.totalAmount) {
        form.paidAmount = 0;
      }
    };

    const handlePaymentStatusChange = () => {
      if (form.paymentStatus === 'paid') {
        form.paidAmount = form.totalAmount;
      } else if (form.paymentStatus === 'pending' && form.paidAmount > form.totalAmount) {
        form.paidAmount = 0;
      }
    };

    const selectPaymentMethod = (value) => {
      form.paymentMethod = value;
    };

    const selectPaymentStatus = (value) => {
      form.paymentStatus = value;
      handlePaymentStatusChange();
    };

    const setCheckoutPaymentMethodBtnRef = (value, el) => {
      if (el) checkoutPaymentMethodBtnRefs.value[value] = el;
      else delete checkoutPaymentMethodBtnRefs.value[value];
    };

    const setCheckoutPaymentStatusBtnRef = (value, el) => {
      if (el) checkoutPaymentStatusBtnRefs.value[value] = el;
      else delete checkoutPaymentStatusBtnRefs.value[value];
    };

    const initCheckoutDefaults = () => {
      form.paymentMethod = 'cash';
      form.paymentStatus = 'paid';
      handlePaymentStatusChange();
      checkoutFocusStage.value = 'payment_method';
    };

    const focusCheckoutStage = async () => {
      if (currentStep.value !== 3) return;
      if (showCouponPicker.value) return;
      await nextTick();
      if (checkoutFocusStage.value === 'payment_method') {
        checkoutPaymentMethodBtnRefs.value.cash?.focus();
      } else if (checkoutFocusStage.value === 'payment_status') {
        checkoutPaymentStatusBtnRefs.value.paid?.focus();
      } else if (checkoutFocusStage.value === 'submit') {
        submitOrderBtnEl.value?.focus();
      }
    };

    const focusStep1NextButton = async () => {
      if (currentStep.value !== 1) return;
      if (showStep2MemberSearch.value) return;
      await nextTick();
      goToStep2BtnEl.value?.focus();
    };

    const handleCheckoutKeydown = (e) => {
      if (currentStep.value !== 3) return;
      if (e.key !== 'Enter') return;
      if (showCouponPicker.value || submitting.value) return;

      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (checkoutFocusStage.value === 'submit') {
        if (e.target === submitOrderBtnEl.value) return;
      }

      if (checkoutFocusStage.value === 'payment_method') {
        e.preventDefault();
        const methodBtn = e.target?.closest?.('.payment-btn[data-value]');
        const methodValue = methodBtn?.dataset?.value;
        if (methodValue && paymentMethodValues.has(methodValue)) {
          form.paymentMethod = methodValue;
        } else if (!form.paymentMethod) {
          form.paymentMethod = 'cash';
        }
        checkoutFocusStage.value = 'payment_status';
        focusCheckoutStage();
        return;
      }

      if (checkoutFocusStage.value === 'payment_status') {
        e.preventDefault();
        const statusBtn = e.target?.closest?.('.payment-btn[data-value]');
        const statusValue = statusBtn?.dataset?.value;
        if (statusValue && paymentStatusValues.has(statusValue)) {
          form.paymentStatus = statusValue;
        } else if (!form.paymentStatus) {
          form.paymentStatus = 'paid';
        }
        handlePaymentStatusChange();
        checkoutFocusStage.value = 'submit';
        focusCheckoutStage();
      }
    };

    const setPaidAmountByStatus = (type) => {
      if (type === 'full') {
        form.paidAmount = form.totalAmount || 0;
        if (!form.paymentStatus) form.paymentStatus = 'paid';
        return;
      }
      form.paidAmount = 0;
      if (!form.paymentStatus) form.paymentStatus = 'pending';
    };

    const calculateItemSubtotal = (index) => {
      const item = form.items[index];
      item.subtotal = Math.max(0, (item.quantity || 0) * (item.unitPrice || 0) - (item.discountAmount || 0));
      calculateTotal();
    };

    const updateItemQuantity = (index, delta) => {
      const item = form.items[index];
      const nextValue = Math.max(1, Number(item.quantity || 1) + delta);
      item.quantity = nextValue;
      calculateItemSubtotal(index);
    };

    const addItem = () => {
      form.items.push({ productName: '', productSku: '', quantity: 1, unitPrice: 0, discountAmount: 0, subtotal: 0 });
    };

    const removeItem = (index) => {
      form.items.splice(index, 1);
      calculateTotal();
    };

    const clearAllItems = () => {
      form.items = [];
      Object.keys(errors).forEach(key => {
        if (key.startsWith('items.') || key === 'totalAmount') delete errors[key];
      });
      calculateTotal();
    };

    // 步驟控制
    const setCustomerType = async (type) => {
      customerType.value = type;
      if (type === 'guest') {
        selectedMember.value = null;
        form.memberId = null;
        selectedOrderMembers.value = [];
        showStep2MemberSearch.value = false;
        selectedCouponCode.value = null;
        availableCoupons.value = [];
        syncOrderDiscount();
      } else {
        showStep2MemberSearch.value = true;
        await nextTick();
        memberSearchRef.value?.focus();
      }
    };

    const toggleStep2MemberSearch = async () => {
      showStep2MemberSearch.value = !showStep2MemberSearch.value;
      if (showStep2MemberSearch.value) {
        await nextTick();
        memberSearchRef.value?.focus();
      }
    };

    const goToStep2 = async () => {
      // 若選擇「會員」但尚未選定會員，提示用戶
      if (customerType.value === 'member' && !form.memberId) {
        await showError('請選擇會員', '請先搜尋並選擇會員，或切換為「訪客結帳」');
        return;
      }
      currentStep.value = 2;
      await nextTick();
      barcodeInputEl.value?.focus();
    };

    const validateStep2ForCheckout = () => {
      Object.keys(errors).forEach(key => delete errors[key]);
      let hasError = false;
      form.items.forEach((item, index) => {
        if (!item.productName || item.productName.trim() === '') {
          errors[`items.${index}.productName`] = '商品名稱不能為空';
          hasError = true;
        }
        if (!item.quantity || item.quantity <= 0) {
          errors[`items.${index}.quantity`] = '數量必須大於 0';
          hasError = true;
        }
        if (item.unitPrice < 0) {
          errors[`items.${index}.unitPrice`] = '單價不能為負數';
          hasError = true;
        }
      });
      if (form.totalAmount <= 0) {
        errors.totalAmount = '總金額必須大於 0';
        hasError = true;
      }
      return !hasError;
    };

    const goToStep3 = async () => {
      if (!validateStep2ForCheckout()) {
        await showError('請先完成商品與金額', '請確認商品內容與總金額後再進入結帳');
        return;
      }
      currentStep.value = 3;
      checkoutItemThumbErrorByIndex.value = {};
      initCheckoutDefaults();
      await loadMemberCoupons();
      syncOrderDiscount();
      fetchPointsEstimate();
      await focusCheckoutStage();
    };

    const openProductPicker = () => { showProductPicker.value = true; };
    const closeProductPicker = async () => {
      showProductPicker.value = false;
      await nextTick();
      focusBarcodeInput();
    };

    const focusBarcodeInput = () => {
      if (currentStep.value !== 2) return;
      if (showProductPicker.value || showQuickVariantPicker.value) return;
      const input = barcodeInputEl.value;
      if (!input) return;
      input.focus();
      input.select?.();
    };

    const goToStep1 = () => {
      currentStep.value = 1;
    };

    const handleStep2Keydown = (e) => {
      if (currentStep.value !== 2) return;
      if (e.key !== 'Backspace') return;
      if (showProductPicker.value || showQuickVariantPicker.value) return;

      const activeEl = document.activeElement;
      if (!activeEl) {
        e.preventDefault();
        goToStep1();
        return;
      }

      const tag = activeEl.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') {
        const input = activeEl;
        const value = String(input.value ?? '');
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const hasSelection = start !== end;
        const canDelete = hasSelection || (start > 0 && value.length > 0);
        if (canDelete) return;
        e.preventDefault();
        goToStep1();
        return;
      }

      if (tag === 'SELECT' || activeEl.isContentEditable) return;

      e.preventDefault();
      goToStep1();
    };

    const handleBarcodeTab = (e) => {
      if (e.key === 'Tab' && e.shiftKey) return;
      e.preventDefault();
      goToStep3BtnEl.value?.focus();
    };

    const handleStep2Interaction = () => {
      window.setTimeout(() => {
        if (currentStep.value !== 2) return;
        if (showProductPicker.value || showQuickVariantPicker.value) return;
        const activeEl = document.activeElement;
        const isEditable = activeEl
          && (activeEl.tagName === 'INPUT'
            || activeEl.tagName === 'TEXTAREA'
            || activeEl.tagName === 'SELECT'
            || activeEl.isContentEditable);
        if (isEditable && activeEl !== barcodeInputEl.value) return;
        focusBarcodeInput();
      }, 0);
    };

    const formatVariantLabel = (variant) => {
      if (variant.attributes && Object.keys(variant.attributes).length > 0) {
        return Object.values(variant.attributes).filter(Boolean).join(' / ');
      }
      return variant.sku || '規格';
    };

    /** 接收 ProductPickerModal 的 select 事件 */
    const onPickerSelect = ({ product, variant }) => {
      if (variant) {
        // 選到規格
        const price = Number(
          variant.specialPrice != null && variant.specialPrice !== ''
            ? variant.specialPrice
            : (variant.unitPrice ?? product.unitPrice)
        ) || 0;
        const label = formatVariantLabel(variant);
        const name = label && label !== variant.sku ? `${product.name} - ${label}` : product.name;
        const sku = variant.sku || product.sku || '';
        const existIndex = form.items.findIndex(i => i.productId === product.id && i.variantId === variant.id);
        if (existIndex >= 0) {
          form.items[existIndex].quantity += 1;
          calculateItemSubtotal(existIndex);
        } else {
          const emptyIndex = form.items.findIndex(i => !i.productName && i.unitPrice === 0 && i.quantity === 1);
          const newItem = {
            productId: product.id,
            variantId: variant.id,
            productName: name,
            productSku: sku,
            imageUrl: extractProductImageUrl(product),
            quantity: 1,
            unitPrice: price,
            discountAmount: 0,
            subtotal: price,
          };
          if (emptyIndex >= 0) { form.items.splice(emptyIndex, 1, newItem); calculateItemSubtotal(emptyIndex); }
          else { form.items.push(newItem); calculateItemSubtotal(form.items.length - 1); }
        }
      } else {
        // 無規格商品（直接加入）
        const price = Number(product.unitPrice) || 0;
        const sku = product.sku || '';
        const existIndex = form.items.findIndex(i => i.productId === product.id && i.productSku === sku);
        if (existIndex >= 0) {
          form.items[existIndex].quantity += 1;
          calculateItemSubtotal(existIndex);
        } else {
          const emptyIndex = form.items.findIndex(i => !i.productName && i.unitPrice === 0 && i.quantity === 1);
          const newItem = {
            productId: product.id,
            productName: product.name,
            productSku: sku,
            imageUrl: extractProductImageUrl(product),
            quantity: 1,
            unitPrice: price,
            discountAmount: 0,
            subtotal: price,
          };
          if (emptyIndex >= 0) { form.items.splice(emptyIndex, 1, newItem); calculateItemSubtotal(emptyIndex); }
          else { form.items.push(newItem); calculateItemSubtotal(form.items.length - 1); }
        }
      }
      calculateTotal();
    };

    // 條碼加入商品
    const handleBarcodeAdd = async () => {
      const barcode = barcodeQuery.value.trim();
      if (!barcode) return;
      barcodeError.value = '';
      barcodeLoading.value = true;
      try {
        const res = await productService.getByBarcode(barcode);
        if (!res || !res.success || !res.data) {
          barcodeError.value = '找不到商品';
          return;
        }
        const d = res.data;
        const price = Number(
          d.specialPrice != null && d.specialPrice !== '' ? d.specialPrice : d.unitPrice
        ) || 0;
        const name = d.productName || d.name || '商品';
        const sku = d.sku || d.productSku || '';
        // 條碼可能命中 variant（有 productId）或 product（有 id）
        const productId = d.productId || d.id || null;
        const variantId = d.type === 'variant' ? d.id : null;
        const existIndex = form.items.findIndex(i =>
          variantId ? i.variantId === variantId : (i.productId === productId && !i.variantId && i.productSku === sku)
        );
        if (existIndex >= 0) {
          form.items[existIndex].quantity += 1;
          calculateItemSubtotal(existIndex);
        } else {
          const emptyIndex = form.items.findIndex(i => !i.productName && i.unitPrice === 0 && i.quantity === 1);
          const newItem = {
            productId,
            variantId,
            productName: name,
            productSku: sku,
            imageUrl: extractProductImageUrl(d),
            quantity: 1,
            unitPrice: price,
            discountAmount: 0,
            subtotal: price,
          };
          if (emptyIndex >= 0) { form.items.splice(emptyIndex, 1, newItem); calculateItemSubtotal(emptyIndex); }
          else { form.items.push(newItem); calculateItemSubtotal(form.items.length - 1); }
        }
        calculateTotal();
        barcodeQuery.value = '';
      } catch (e) {
        barcodeError.value = e.response?.data?.message || e.message || '查詢失敗';
      } finally {
        barcodeLoading.value = false;
      }
    };

    // 載入門市列表
    const saveLastStore = () => {
      if (form.storeId) {
        localStorage.setItem('order_last_store_id', form.storeId);
      } else {
        localStorage.removeItem('order_last_store_id');
      }
    };

    const fetchStores = async () => {
      storesLoading.value = true;
      try {
        const response = await storeService.getStores({ pageSize: 100, status: 'active' });
        if (response && response.success && response.data) {
          stores.value = response.data.items || [];
          // 驗證記憶的門市是否仍在清單中，若已不存在則清除
          if (form.storeId && !stores.value.find(s => s.id === form.storeId)) {
            form.storeId = '';
            localStorage.removeItem('order_last_store_id');
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        storesLoading.value = false;
      }
    };

    const fetchQuickCategories = async () => {
      try {
        const response = await productService.getCategoriesFlat();
        if (!response?.success || !response.data) return;
        quickCategories.value = (response.data || []).map(item => ({
          id: item.id,
          name: item.name || item.categoryName || '未命名分類'
        }));
      } catch (err) {
        console.error('載入商品分類失敗:', err);
      }
    };

    const fetchQuickProducts = async () => {
      quickProductsLoading.value = true;
      quickProductsError.value = '';
      quickProductThumbErrorById.value = {};
      try {
        const pageSize = 100;
        let page = 1;
        let totalPages = 1;
        const allItems = [];

        while (page <= totalPages) {
          const response = await productService.getProducts({ page, pageSize });
          const payload = response?.data;
          if (!response?.success || !payload) {
            quickProducts.value = [];
            quickProductsError.value = response?.message || '載入商品失敗';
            return;
          }

          if (Array.isArray(payload)) {
            allItems.push(...payload);
            totalPages = 1;
          } else {
            const items = payload.items || payload.data?.items || payload.results || [];
            if (Array.isArray(items)) {
              allItems.push(...items);
            }
            totalPages = Number(payload.pagination?.totalPages || 1);
          }

          page += 1;
        }

        quickProducts.value = allItems.filter(p => (p.status || 'active') === 'active');
      } catch (err) {
        console.error('載入快速商品清單失敗:', err);
        quickProducts.value = [];
        quickProductsError.value = err.response?.data?.message || err.message || '載入商品失敗';
      } finally {
        quickProductsLoading.value = false;
      }
    };

    const handleMemberSelected = (member) => {
      selectedMember.value = member;
      form.memberId = member ? member.id : null;
      showStep2MemberSearch.value = false;
      selectedCouponCode.value = null;
      if (currentStep.value === 3) {
        loadMemberCoupons();
      } else {
        syncOrderDiscount();
      }
    };

    const handleMemberCleared = () => {
      selectedOrderMembers.value = [];
      selectedMember.value = null;
      form.memberId = null;
      selectedCouponCode.value = null;
      availableCoupons.value = [];
      syncOrderDiscount();
    };

    const validate = () => {
      Object.keys(errors).forEach(key => delete errors[key]);
      let hasError = false;
      form.items.forEach((item, index) => {
        if (!item.productName || item.productName.trim() === '') {
          errors[`items.${index}.productName`] = '商品名稱不能為空';
          hasError = true;
        }
        if (!item.quantity || item.quantity <= 0) {
          errors[`items.${index}.quantity`] = '數量必須大於 0';
          hasError = true;
        }
        if (item.unitPrice < 0) {
          errors[`items.${index}.unitPrice`] = '單價不能為負數';
          hasError = true;
        }
      });
      if (form.totalAmount <= 0) { errors.totalAmount = '總金額必須大於 0'; hasError = true; }
      if (form.paidAmount < 0) { errors.paidAmount = '已付金額不能為負數'; hasError = true; }
      if (form.paymentStatus === 'paid' && form.paidAmount < form.totalAmount) {
        errors.paidAmount = '付款狀態為已付款時，已付金額需等於或大於總金額';
        hasError = true;
      }
      if (form.paymentStatus !== 'paid' && form.paidAmount > form.totalAmount) {
        errors.paidAmount = '未付款或非已付款狀態時，已付金額不可大於總金額';
        hasError = true;
      }
      if (!form.paymentMethod) { errors.paymentMethod = '請選擇付款方式'; hasError = true; }
      if (!form.paymentStatus) { errors.paymentStatus = '請選擇付款狀態'; hasError = true; }
      return !hasError;
    };

    const buildOrderData = (allowBackorder = false) => ({
      memberId: form.memberId || null,
      storeId: form.storeId || null,
      orderType: form.orderType,
      orderSource: form.orderSource,
      subtotal: calculatedSubtotal.value,
      discountAmount: form.discountAmount || 0,
      couponCode: selectedCouponCode.value || null,
      shippingFee: form.shippingFee || 0,
      deliveryFee: 0,
      taxAmount: form.taxAmount || 0,
      totalAmount: form.totalAmount,
      paidAmount: form.paidAmount,
      paymentMethod: form.paymentMethod || null,
      paymentStatus: form.paymentStatus,
      status: form.status,
      orderDate: new Date(form.orderDate).toISOString(),
      remarks: form.remarks || null,
      allowBackorder,
      items: form.items.map(item => ({
        productId: item.productId || null,
        variantId: item.variantId || null,
        productName: item.productName,
        productSku: item.productSku || null,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountAmount: item.discountAmount || 0,
        subtotal: item.subtotal
      }))
    });

    const shouldPromptCouponUse = async () => {
      if (currentStep.value !== 3 || !form.memberId || selectedCouponCode.value) return false;
      if (couponsLoading.value) {
        await loadMemberCoupons();
      }
      return eligibleCouponCount.value > 0;
    };

    const closeCouponUsePrompt = () => {
      showCouponUsePrompt.value = false;
    };

    const focusCouponPromptYesButton = async () => {
      await nextTick();
      couponPromptYesBtnEl.value?.focus();
    };

    const handleCouponPromptKeydown = (e) => {
      if (!showCouponUsePrompt.value) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const noBtn = couponPromptNoBtnEl.value;
      const yesBtn = couponPromptYesBtnEl.value;
      if (!noBtn || !yesBtn) return;
      if (e.key === 'ArrowRight') {
        if (document.activeElement === noBtn) yesBtn.focus();
        else noBtn.focus();
        return;
      }
      if (document.activeElement === yesBtn) noBtn.focus();
      else yesBtn.focus();
    };

    const acceptCouponUse = () => {
      showCouponUsePrompt.value = false;
      pendingSubmitAfterCoupon.value = true;
      openCouponPicker();
    };

    const declineCouponUseAndSubmit = async () => {
      showCouponUsePrompt.value = false;
      await openOrderConfirm();
    };

    const refreshPointsEstimate = async () => {
      if (!form.memberId) {
        pointsEstimate.value = null;
        return;
      }
      pointsEstimateLoading.value = true;
      try {
        const response = await orderService.estimatePoints({
          memberId: form.memberId || null,
          totalAmount: form.totalAmount || 0,
          paidAmount: form.paidAmount || 0,
          paymentStatus: form.paymentStatus || null,
          orderDate: form.orderDate ? new Date(form.orderDate).toISOString() : null,
        });
        pointsEstimate.value = response?.success ? (response.data || null) : null;
      } catch (err) {
        console.error('點數試算失敗:', err);
        pointsEstimate.value = null;
      } finally {
        pointsEstimateLoading.value = false;
      }
    };

    const openOrderConfirm = async () => {
      syncOrderDiscount();
      await refreshPointsEstimate();
      showOrderConfirm.value = true;
      await nextTick();
      orderConfirmSubmitBtnEl.value?.focus();
    };

    const closeOrderConfirm = async () => {
      showOrderConfirm.value = false;
      checkoutFocusStage.value = 'submit';
      await nextTick();
      submitOrderBtnEl.value?.focus();
    };

    const handleOrderConfirmKeydown = (e) => {
      if (!showOrderConfirm.value || submitting.value) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const cancelBtn = orderConfirmCancelBtnEl.value;
      const submitBtn = orderConfirmSubmitBtnEl.value;
      if (!cancelBtn || !submitBtn) return;
      if (e.key === 'ArrowRight') {
        if (document.activeElement === cancelBtn) submitBtn.focus();
        else cancelBtn.focus();
        return;
      }
      if (document.activeElement === submitBtn) cancelBtn.focus();
      else submitBtn.focus();
    };

    const confirmOrderSubmit = async () => {
      showOrderConfirm.value = false;
      await submitOrderCore();
    };

    const submitOrderCore = async () => {
      submitting.value = true;
      try {
        const response = await orderService.createOrder(buildOrderData(false));
        if (response && response.success) {
          await showSuccess('訂單建立成功', '訂單已建立');
          router.push('/client/orders');
        } else {
          throw new Error(response?.message || '建立訂單失敗');
        }
      } catch (err) {
        // 庫存不足 409：彈出缺貨確認對話框
        const errData = err.response?.data;
        if (err.response?.status === 409 || errData?.error?.code === 'INSUFFICIENT_STOCK') {
          submitting.value = false;
          const insufficient = errData?.error?.details?.insufficient || [];
          const itemList = insufficient.map(i =>
            `• ${i.productName || i.variantName || i.variantId}：需要 ${i.needed}，可用 ${i.available}`
          ).join('\n');

          const result = await Swal.fire({
            title: '庫存不足',
            html: `<div style="text-align:left;white-space:pre-line;font-size:14px">${itemList}</div><p style="margin-top:12px">是否先下單，後續再補貨？</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: '確認下單（缺貨下訂）',
            cancelButtonText: '取消',
            confirmButtonColor: '#e09900',
          });

          if (result.isConfirmed) {
            submitting.value = true;
            try {
              const resp2 = await orderService.createOrder(buildOrderData(true));
              if (resp2 && resp2.success) {
                await showSuccess('訂單已建立', '此訂單已標記為缺貨下訂，請儘速補貨');
                router.push('/client/orders');
              } else {
                throw new Error(resp2?.message || '建立訂單失敗');
              }
            } catch (err2) {
              console.error(err2);
              await showError('建立失敗', err2.response?.data?.message || err2.message || '建立訂單時發生錯誤');
            } finally {
              submitting.value = false;
            }
          }
          return;
        }
        console.error(err);
        await showError('建立失敗', errData?.message || err.message || '建立訂單時發生錯誤');
      } finally {
        submitting.value = false;
      }
    };

    const handleSubmit = async () => {
      if (!validate()) return;
      if (await shouldPromptCouponUse()) {
        showCouponUsePrompt.value = true;
        await focusCouponPromptYesButton();
        return;
      }
      await openOrderConfirm();
    };

    const handleCancel = () => router.push('/client/orders');

    const handleOrderCreateEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (submitting.value) return;
      if (showOrderConfirm.value) {
        e.preventDefault();
        closeOrderConfirm();
        return;
      }
      if (showCouponUsePrompt.value) {
        e.preventDefault();
        closeCouponUsePrompt();
        return;
      }
      if (showCouponPicker.value) {
        e.preventDefault();
        closeCouponPicker();
        return;
      }
      e.preventDefault();
      handleCancel();
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return '$0';
      return `$${Number(amount).toLocaleString('zh-TW', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    };

    const formatPointsEstimate = (points) => {
      const value = Number(points);
      if (!Number.isFinite(value) || value <= 0) return '0 點';
      return `${value.toLocaleString('zh-TW')} 點`;
    };

    const fetchPointsEstimate = () => {
      if (currentStep.value !== 3) return;

      if (pointsEstimateTimer) {
        clearTimeout(pointsEstimateTimer);
      }

      pointsEstimateTimer = setTimeout(async () => {
        pointsEstimateLoading.value = true;
        try {
          const response = await orderService.estimatePoints({
            memberId: form.memberId || null,
            totalAmount: form.totalAmount || 0,
            paidAmount: form.paidAmount || 0,
            paymentStatus: form.paymentStatus || null,
            orderDate: form.orderDate ? new Date(form.orderDate).toISOString() : null,
          });
          pointsEstimate.value = response?.success ? (response.data || null) : null;
        } catch (err) {
          console.error('點數試算失敗:', err);
          pointsEstimate.value = null;
        } finally {
          pointsEstimateLoading.value = false;
        }
      }, 250);
    };

    const paymentDiff = computed(() => (form.paidAmount || 0) - (form.totalAmount || 0));
    const outstandingAmount = computed(() => Math.max((form.totalAmount || 0) - (form.paidAmount || 0), 0));

    const quickAddProduct = async (product) => {
      if (!product?.id) return;
      quickAddLoadingByProductId[product.id] = true;
      try {
        const variantsRes = await productService.listVariants(product.id);
        const variants = Array.isArray(variantsRes?.data)
          ? variantsRes.data
          : (Array.isArray(variantsRes?.data?.items) ? variantsRes.data.items : []);
        if (variants.length > 1) {
          quickVariantProduct.value = product;
          quickVariantOptions.value = variants;
          showQuickVariantPicker.value = true;
          return;
        }
        if (variants.length === 1) {
          onPickerSelect({ product, variant: variants[0] });
          return;
        }
        onPickerSelect({ product });
      } catch (err) {
        console.error(err);
        await showError('加入商品失敗', err.response?.data?.message || err.message || '請稍後再試');
      } finally {
        quickAddLoadingByProductId[product.id] = false;
      }
    };

    const closeQuickVariantPicker = () => {
      showQuickVariantPicker.value = false;
      quickVariantProduct.value = null;
      quickVariantOptions.value = [];
      nextTick(() => {
        focusBarcodeInput();
      });
    };

    const handleQuickVariantSelect = (variant) => {
      if (!quickVariantProduct.value) return;
      onPickerSelect({ product: quickVariantProduct.value, variant });
      closeQuickVariantPicker();
    };

    const fetchInventorySettings = async () => {
      try {
        const res = await clientSettingsService.get('inventory');
        inventorySettings.value = res.data?.settings || null;
      } catch (e) {
        // 模組未啟用時靜默忽略
      }
    };

    // Wixtar 客戶的訂單一律由 Wixtar POS 建立並經 Webhook 同步回 CRM，
    // 在 CRM 自行建單會與 Wixtar 回推的訂單重複；後端亦會擋下 POST /orders。
    const redirectIfWixtarClient = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return false;
      try {
        const res = await clientService.getClient(user.clientId);
        if (res?.data?.clientData?.memberSystemType !== 'WIXTAR') return false;
      } catch (err) {
        console.error('載入客戶會員系統類型失敗:', err);
        return false;
      }
      await showError('此客戶使用 Wixtar 會員系統，訂單請於 Wixtar 建立，CRM 僅保留同步後的副本');
      router.replace('/client/orders');
      return true;
    };

    onMounted(async () => {
      if (await redirectIfWixtarClient()) return;
      window.addEventListener('keydown', handleOrderCreateEscape, true);
      await Promise.all([fetchStores(), fetchInventorySettings(), fetchQuickCategories(), fetchQuickProducts()]);
      syncOrderDiscount();
      await focusStep1NextButton();
    });

    onUnmounted(() => {
      window.removeEventListener('keydown', handleOrderCreateEscape, true);
    });

    watch(
      () => [
        currentStep.value,
        form.memberId,
        form.totalAmount,
        form.paidAmount,
        form.paymentStatus,
        form.orderDate,
      ],
      () => {
        if (currentStep.value === 3) {
          fetchPointsEstimate();
        }
      }
    );

    watch(
      [calculatedSubtotal, selectedCouponCode, manualOrderDiscount, () => form.shippingFee, () => form.taxAmount],
      () => {
        if (selectedCouponCode.value && selectedCoupon.value) {
          const min = Number(selectedCoupon.value.minPurchaseAmount || 0);
          if (calculatedSubtotal.value < min) {
            selectedCouponCode.value = null;
          }
        }
        syncOrderDiscount();
        if (currentStep.value === 3) {
          fetchPointsEstimate();
        }
      }
    );

    watch(() => form.totalAmount, () => {
      calculatePaidAmount();
    });

    watch(currentStep, async (step) => {
      if (step === 1) {
        await focusStep1NextButton();
        return;
      }
      if (step !== 2) return;
      if (!quickProducts.value.length) {
        await fetchQuickProducts();
      }
    });

    return {
      currentStep,
      customerType,
      pageTitle,
      submitting,
      selectedOrderMembers,
      selectedMember,
      stores,
      storesLoading,
      inventorySettings,
      inventoryEnabled,
      storeWarehouseMode,
      selectedStoreWarehouseId,
      barcodeQuery,
      barcodeLoading,
      barcodeError,
      quickProductsLoading,
      quickProductsError,
      quickCategories,
      selectedQuickCategoryId,
      quickSearchQuery,
      filteredQuickProducts,
      hasOrderItems,
      quickAddLoadingByProductId,
      showQuickVariantPicker,
      quickVariantProduct,
      quickVariantOptions,
      showAmountDetails,
      showStep2MemberSearch,
      showProductPicker,
      memberSearchRef,
      barcodeInputEl,
      form,
      errors,
      calculatedSubtotal,
      orderTypeLabel,
      paymentMethods,
      paymentStatuses,
      goToStep2BtnEl,
      goToStep3BtnEl,
      submitOrderBtnEl,
      checkoutFocusStage,
      focusStep1NextButton,
      handleBarcodeTab,
      setCheckoutPaymentMethodBtnRef,
      setCheckoutPaymentStatusBtnRef,
      selectPaymentMethod,
      selectPaymentStatus,
      handleCheckoutKeydown,
      setCustomerType,
      saveLastStore,
      goToStep2,
      goToStep1,
      goToStep3,
      handleStep2Interaction,
      handleStep2Keydown,
      openProductPicker,
      closeProductPicker,
      onPickerSelect,
      quickAddProduct,
      fetchQuickProducts,
      closeQuickVariantPicker,
      handleQuickVariantSelect,
      getQuickProductThumb,
      shouldShowQuickProductThumb,
      handleQuickProductThumbError,
      getCheckoutItemThumb,
      shouldShowCheckoutItemThumb,
      handleCheckoutItemThumbError,
      formatVariantLabel,
      addItem,
      removeItem,
      clearAllItems,
      updateItemQuantity,
      calculateItemSubtotal,
      calculateTotal,
      calculatePaidAmount,
      setPaidAmountByStatus,
      handlePaymentStatusChange,
      handleBarcodeAdd,
      handleMemberSelected,
      handleMemberCleared,
      toggleStep2MemberSearch,
      handleSubmit,
      handleCancel,
      formatCurrency,
      paymentDiff,
      outstandingAmount,
      pointsEstimate,
      pointsEstimateLoading,
      formatPointsEstimate,
      fetchPointsEstimate,
      manualOrderDiscount,
      selectedCouponCode,
      selectedCoupon,
      couponDiscountAmount,
      couponFreeShipping,
      checkoutCouponOptions,
      eligibleCouponCount,
      couponsLoading,
      couponsError,
      showCouponPicker,
      couponPickerModalEl,
      handleCouponPickerKeydown,
      showCouponUsePrompt,
      showOrderConfirm,
      couponPromptNoBtnEl,
      couponPromptYesBtnEl,
      orderConfirmCancelBtnEl,
      orderConfirmSubmitBtnEl,
      closeCouponUsePrompt,
      handleCouponPromptKeydown,
      closeOrderConfirm,
      handleOrderConfirmKeydown,
      confirmOrderSubmit,
      acceptCouponUse,
      declineCouponUseAndSubmit,
      syncOrderDiscount,
      clearSelectedCoupon,
      openCouponPicker,
      closeCouponPicker,
      selectCouponAndClose,
      clearSelectedCouponAndClose,
      getCouponTypeText,
      formatCouponBenefit,
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.create-order-container {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 20px;
  padding-top: 72px;
  box-sizing: border-box;
}

.create-order-container.is-pos-step,
.create-order-container.is-checkout-step {
  display: flex;
  flex-direction: column;
  /* 100vh - topbar(56) - page-content padding(16) */
  height: calc(100vh - 72px);
  overflow: hidden;
}

.create-order-container.is-pos-step .form-container,
.create-order-container.is-checkout-step .form-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-bottom: 0;
}

/* 結帳步驟：鍵盤 focus 高亮（Enter 快速結帳流程） */
@keyframes checkout-focus-ring {
  0%, 100% {
    box-shadow:
      0 0 0 3px #fff,
      0 0 0 6px rgba(245, 158, 11, 0.95),
      0 4px 14px rgba(245, 158, 11, 0.35);
  }
  50% {
    box-shadow:
      0 0 0 3px #fff,
      0 0 0 9px rgba(245, 158, 11, 0.55),
      0 4px 18px rgba(245, 158, 11, 0.45);
  }
}

.create-order-container.is-checkout-step .payment-btn:focus,
.create-order-container.is-checkout-step .payment-btn:focus-visible {
  outline: none;
  border-color: #f59e0b;
  position: relative;
  z-index: 2;
  animation: checkout-focus-ring 1.1s ease-in-out infinite;
}

.create-order-container.is-checkout-step .payment-btn.active:focus,
.create-order-container.is-checkout-step .payment-btn.active:focus-visible {
  border-color: #fde68a;
  color: #fff;
}

.create-order-container.is-basic-step .header-actions .btn-primary:focus,
.create-order-container.is-basic-step .header-actions .btn-primary:focus-visible,
.create-order-container.is-pos-step .header-actions .btn-primary:focus,
.create-order-container.is-pos-step .header-actions .btn-primary:focus-visible,
.create-order-container.is-checkout-step .header-actions .btn-primary:focus,
.create-order-container.is-checkout-step .header-actions .btn-primary:focus-visible {
  outline: none;
  animation: checkout-focus-ring 1.1s ease-in-out infinite;
  transform: translateY(-1px);
}

.create-order-container .page-header {
  background: #f5f7fa;
  padding: 10px 20px;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.create-order-container .page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

/* 步驟指示器 */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
  gap: 0;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.step-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid #d0d0d0;
  background: #fff;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.2s;
}

.step.active .step-circle {
  border-color: #4f80e1;
  background: #4f80e1;
  color: #fff;
}

.step.done .step-circle {
  border-color: #28a745;
  background: #28a745;
  color: #fff;
}

.step-label {
  font-size: 13px;
  color: #999;
  white-space: nowrap;
}

.step.active .step-label,
.step.done .step-label {
  color: #333;
  font-weight: 500;
}

.step-line {
  flex: 1;
  height: 2px;
  background: #d0d0d0;
  margin: 0 12px;
  margin-bottom: 20px;
  min-width: 60px;
  max-width: 120px;
  transition: background 0.2s;
}

.step-line.done {
  background: #28a745;
}

/* 表單卡片 */
.form-container {
  background: white;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #222;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 500;
  margin-bottom: 6px;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-text {
  font-size: 12px;
  color: #888;
  margin-top: 4px;
}

.mt-12 { margin-top: 12px; }

/* 訪客/會員切換 */
.customer-type-row {
  display: flex;
  gap: 10px;
}

.type-btn {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s;
}

.type-btn:hover {
  border-color: #4f80e1;
  color: #4f80e1;
}

.type-btn.active {
  border-color: #4f80e1;
  background: #eef3fd;
  color: #2a5bd7;
}

.type-btn i {
  font-size: 16px;
}

.guest-notice {
  margin-top: 12px;
  padding: 10px 14px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 13px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 步驟操作按鈕 */
.step-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
}

.header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* 步驟 1 摘要 */
.step1-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8faff;
  border: 1px solid #d8e4f8;
  border-radius: 8px;
  padding: 10px 16px;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
}

.summary-items {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.summary-item {
  font-size: 13px;
  color: #444;
  display: flex;
  align-items: center;
  gap: 4px;
}

.summary-item i {
  color: #4f80e1;
}

.btn-back-step {
  background: none;
  border: 1px solid #4f80e1;
  color: #4f80e1;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.btn-back-step:hover {
  background: #eef3fd;
}

.step2-customer-inline-edit {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.step2-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.step2-member-search {
  margin-bottom: 14px;
  flex-shrink: 0;
}

.step1-summary.compact {
  margin-bottom: 0;
  flex: 1;
  min-width: 240px;
}

.customer-type-row.compact {
  gap: 8px;
}

.customer-type-row.compact .type-btn {
  min-height: 34px;
  padding: 8px 12px;
  flex: 0 0 auto;
}

.step2-member-compact {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.step2-member-label {
  font-size: 12px;
  color: #334155;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 步驟二兩欄排版 */
.step2-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(360px, 1fr);
  gap: 20px;
  align-items: stretch;
}

.pos-screen {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.pos-screen > form {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

.pos-screen .step2-layout {
  flex: 1;
  min-height: 0;
  gap: 16px;
}

.step2-left {
  display: flex;
  min-width: 0;
  min-height: 0;
}

.step2-left .form-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-bottom: 0;
}

.pos-screen .step2-left .form-section {
  height: auto;
}

.step2-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  min-height: 0;
}

.step2-right > .amount-card:first-child {
  flex-shrink: 0;
}

.checkout-step {
  max-width: none;
  margin: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.checkout-form .amount-card {
  margin-bottom: 0;
}

.checkout-form {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.checkout-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(360px, 1fr);
  gap: 16px;
  align-items: stretch;
  flex: 1;
  min-height: 0;
}

.checkout-left,
.checkout-right {
  min-width: 0;
  min-height: 0;
}

.checkout-right {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

.checkout-amount-summary {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overflow-x: hidden;
}

.checkout-right > .amount-card.mt-12 {
  flex-shrink: 0;
}

.checkout-right .amount-card {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

.checkout-left {
  display: flex;
  min-height: 0;
}

.checkout-left .amount-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.checkout-total-row {
  align-items: baseline;
}

.checkout-total-row .amount-label {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.checkout-total-value {
  font-size: 30px;
  font-weight: 800;
  color: #1d4ed8;
  line-height: 1;
}

.points-estimate-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 4px;
}

.points-estimate-value-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 0;
}

.points-estimate-value {
  font-size: 18px;
  font-weight: 700;
  color: #059669;
  line-height: 1.2;
}

.points-estimate-value.is-zero {
  color: #64748b;
}

.points-estimate-hint {
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
  text-align: right;
}

.points-estimate-loading {
  font-size: 13px;
  color: #94a3b8;
}

.amount-value--discount {
  color: #dc2626;
}

.checkout-coupon-row {
  margin: 4px 0 10px;
  box-sizing: border-box;
}

.checkout-coupon-clear-row {
  display: flex;
  justify-content: flex-end;
  margin: -4px 0 8px;
  padding-right: 2px;
}

.checkout-coupon-trigger {
  width: 100%;
  border: 1.5px solid #fb923c;
  border-radius: 8px;
  background: #fff;
  padding: 7px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  text-align: left;
  color: #9a3412;
  font-size: 13px;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  box-sizing: border-box;
}

.checkout-coupon-trigger-label {
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  color: #c2410c;
  white-space: nowrap;
  flex-shrink: 0;
}

.checkout-coupon-trigger-label i {
  font-size: 15px;
  color: #ea580c;
}

.checkout-coupon-arrow {
  flex-shrink: 0;
  margin-left: auto;
}

.checkout-coupon-trigger.is-available {
  background: #fff;
  border-color: #f97316;
  box-shadow: 0 0 0 1px rgba(249, 115, 22, 0.15);
}

.checkout-coupon-trigger.is-available:hover:not(:disabled) {
  background: #fff7ed;
  border-color: #ea580c;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.18);
}

.checkout-coupon-trigger.is-selected {
  background: #ecfdf5;
  border-color: #10b981;
  box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.15);
}

.checkout-coupon-trigger.is-selected:hover:not(:disabled) {
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.18);
}

.checkout-coupon-trigger.is-empty {
  background: #f8fafc;
  border-color: #cbd5e1;
  color: #64748b;
  cursor: default;
}

.checkout-coupon-trigger:hover:not(:disabled):not(.is-empty) {
  border-color: #ea580c;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.18);
}

.checkout-coupon-trigger:disabled {
  cursor: default;
  opacity: 0.85;
}

.checkout-coupon-trigger-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: #7c3aed;
}

.checkout-coupon-trigger-sub {
  font-weight: 500;
  color: #059669;
}

.checkout-coupon-trigger.is-selected .checkout-coupon-trigger-text {
  color: #047857;
}

.checkout-coupon-trigger i {
  flex-shrink: 0;
  font-size: 16px;
  color: #ea580c;
}

.coupon-picker-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.coupon-prompt-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1250;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.coupon-prompt-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.24);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.coupon-prompt-header h4 {
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #c2410c;
}

.coupon-prompt-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #475569;
}

.coupon-prompt-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.coupon-prompt-actions .btn:focus,
.coupon-prompt-actions .btn:focus-visible,
.coupon-prompt-btn:focus,
.coupon-prompt-btn:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 3px #fff,
    0 0 0 6px rgba(245, 158, 11, 0.95);
}

.order-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.order-confirm-modal {
  width: 100%;
  max-width: 560px;
  max-height: min(88vh, 760px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.order-confirm-header {
  padding: 18px 20px 12px;
  border-bottom: 1px solid #eef2f7;
  flex-shrink: 0;
}

.order-confirm-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
}

.order-confirm-subtitle {
  margin: 8px 0 0;
  font-size: 13px;
  color: #64748b;
}

.order-confirm-due {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 20px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  flex-shrink: 0;
}

.order-confirm-due-label {
  font-size: 14px;
  font-weight: 600;
  color: #b91c1c;
  letter-spacing: 0.04em;
}

.order-confirm-due-value {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;
  color: #dc2626;
}

.order-confirm-body {
  padding: 16px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-confirm-section-title {
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.order-confirm-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-confirm-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 8px;
}

.order-confirm-item--gift {
  background: #fff7ed;
}

.order-confirm-item-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.order-confirm-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
}

.order-confirm-item-meta {
  font-size: 12px;
  color: #64748b;
}

.order-confirm-item-calc {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: 12px;
  color: #64748b;
}

.order-confirm-item-calc strong {
  font-size: 14px;
  color: #0f172a;
}

.order-confirm-coupon {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  background: #fff7ed;
  border-radius: 8px;
}

.order-confirm-coupon-name {
  font-size: 14px;
  font-weight: 600;
  color: #c2410c;
}

.order-confirm-coupon-benefit,
.order-confirm-coupon-discount {
  font-size: 13px;
  color: #475569;
}

.order-confirm-points {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-confirm-points strong {
  font-size: 16px;
  color: #0f172a;
}

.order-confirm-points strong.is-zero {
  color: #94a3b8;
}

.order-confirm-points small {
  font-size: 12px;
  color: #64748b;
}

.order-confirm-empty {
  margin: 0;
  font-size: 13px;
  color: #94a3b8;
}

.order-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid #eef2f7;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.order-confirm-actions .btn:focus,
.order-confirm-actions .btn:focus-visible,
.order-confirm-action-btn:focus,
.order-confirm-action-btn:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 3px #fff,
    0 0 0 6px rgba(245, 158, 11, 0.95);
}

.coupon-picker-modal {
  width: 100%;
  max-width: 480px;
  max-height: min(80vh, 640px);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.24);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.coupon-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
}

.coupon-picker-header h4 {
  margin: 0;
  font-size: 16px;
  color: #c2410c;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.coupon-picker-header h4::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 16px;
  border-radius: 2px;
  background: #f97316;
}

.coupon-picker-close {
  border: none;
  background: transparent;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
}

.coupon-picker-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 4px;
}

.coupon-picker-footer-btn:focus,
.coupon-picker-footer-btn:focus-visible {
  outline: none;
  box-shadow:
    0 0 0 3px #fff,
    0 0 0 6px rgba(245, 158, 11, 0.95);
}

.btn-link-sm {
  border: none;
  background: none;
  padding: 0;
  font-size: 12px;
  color: #2563eb;
  cursor: pointer;
}

.btn-link-sm:hover {
  text-decoration: underline;
}

.checkout-coupon-status {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.checkout-coupon-status.is-error {
  color: #dc2626;
}

.checkout-coupon-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.checkout-coupon-item {
  border: 1px solid #fed7aa;
  border-radius: 8px;
  background: #fffbeb;
}

.checkout-coupon-item:not(.is-disabled) {
  border-left: 3px solid #f97316;
}

.checkout-coupon-item.is-selected {
  border-color: #10b981;
  border-left-color: #10b981;
  background: #ecfdf5;
}

.checkout-coupon-item.is-disabled {
  opacity: 0.55;
}

.checkout-coupon-label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  margin: 0;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.checkout-coupon-label:focus,
.checkout-coupon-label:focus-visible {
  outline: none;
  box-shadow:
    inset 0 0 0 2px #f59e0b,
    0 0 0 3px rgba(245, 158, 11, 0.35);
  border-radius: 8px;
}

.checkout-coupon-item.is-disabled .checkout-coupon-label {
  cursor: not-allowed;
}

.checkout-coupon-radio {
  width: 16px;
  height: 16px;
  margin-top: 2px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
}

.checkout-coupon-item.is-selected .checkout-coupon-radio {
  border-color: #10b981;
}

.checkout-coupon-item.is-selected .checkout-coupon-radio::after {
  content: '';
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: #10b981;
}

.checkout-coupon-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.checkout-coupon-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
}

.checkout-coupon-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.checkout-coupon-type {
  display: inline-flex;
  padding: 1px 6px;
  border-radius: 999px;
  background: #ffedd5;
  color: #c2410c;
  font-size: 11px;
  font-weight: 600;
}

.checkout-coupon-preview {
  color: #dc2626;
  font-weight: 600;
}

.checkout-coupon-ineligible {
  font-size: 11px;
  color: #b45309;
}

.gift-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-right: 6px;
  padding: 1px 7px;
  font-size: 11px;
  background: #d1fae5;
  color: #065f46;
  border-radius: 10px;
  font-weight: 600;
  white-space: nowrap;
}

/* Step 2 贈品預覽項目 */
.gift-preview-item {
  background: #f0fdf4;
  border: 1px dashed #86efac;
  border-radius: 6px;
  padding: 8px;
  margin-top: 6px;
}

.gift-item-name {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: #065f46;
}

.gift-item-sku {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  padding-left: 2px;
}

.gift-item-value {
  font-size: 14px;
  color: #065f46;
  font-weight: 500;
}

/* Step 3 結帳商品明細贈品列 */
.checkout-item-row.checkout-gift-row {
  background: #dcfce7;
  border: 1px solid #4ade80;
  border-radius: 8px;
}

.checkout-item-thumb--gift {
  background: #d1fae5;
  color: #065f46;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checkout-items-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
  flex: 1;
  min-height: 0;
}

.checkout-item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.checkout-item-thumb-wrap {
  flex-shrink: 0;
}

.checkout-item-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: block;
}

.checkout-item-thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 18px;
}

.checkout-item-main {
  min-width: 0;
  flex: 1;
}

.checkout-item-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.checkout-item-meta {
  margin-top: 2px;
  font-size: 11px;
  color: #64748b;
}

.checkout-item-calc {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #334155;
  white-space: nowrap;
}

.checkout-item-discount {
  color: #b45309;
}

/* 條碼列 */
.barcode-add-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.barcode-input {
  flex: 1;
  min-width: 120px;
}

.barcode-error {
  color: #dc3545;
  font-size: 13px;
  width: 100%;
}

.quick-select-panel {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.pos-screen .quick-select-panel {
  height: 100%;
  min-height: 0;
}

.quick-select-tip {
  font-size: 12px;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
}

.quick-select-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.quick-select-header h4 {
  margin: 0;
  font-size: 14px;
  color: #334155;
}

.quick-search-input {
  max-width: 220px;
}

.quick-categories {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.quick-cat-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #334155;
  border-radius: 999px;
  font-size: 12px;
  padding: 4px 10px;
  cursor: pointer;
}

.quick-cat-btn.active {
  border-color: #4f80e1;
  background: #eef3fd;
  color: #2a5bd7;
}

.quick-products-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow: auto;
  align-content: start;
}

.quick-product-card {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  text-align: left;
  padding: 8px 10px;
  cursor: pointer;
}

.quick-product-thumb-wrap {
  width: 64px;
  margin-bottom: 6px;
}

.quick-product-thumb {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: block;
  background: #f3f4f6;
}

.quick-product-thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 20px;
}

.quick-product-card:hover:not(:disabled) {
  border-color: #4f80e1;
  box-shadow: 0 0 0 2px rgba(79, 128, 225, 0.12);
}

.quick-product-card:disabled {
  opacity: 0.6;
  cursor: wait;
}

.quick-product-name {
  font-size: 13px;
  color: #1f2937;
  font-weight: 600;
  line-height: 1.35;
  margin-bottom: 6px;
}

.quick-product-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.quick-product-sku {
  font-size: 11px;
  color: #6b7280;
}

.quick-product-price {
  font-size: 12px;
  color: #0f172a;
  font-weight: 600;
}

.quick-products-loading,
.quick-products-empty {
  font-size: 12px;
  color: #6b7280;
  padding: 8px 2px;
}

.quick-products-error {
  font-size: 12px;
  color: #b91c1c;
  padding: 8px 2px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.quick-variant-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.quick-variant-modal {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.24);
  padding: 14px;
}

.quick-variant-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.quick-variant-header h4 {
  margin: 0;
  font-size: 16px;
}

.quick-variant-close {
  border: none;
  background: transparent;
  font-size: 20px;
  color: #64748b;
  cursor: pointer;
}

.quick-variant-product {
  margin: 0 0 10px 0;
  font-size: 13px;
  color: #475569;
}

.quick-variant-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow: auto;
}

.quick-variant-item {
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  background: #fff;
  padding: 9px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #1f2937;
  cursor: pointer;
}

.quick-variant-item:hover {
  border-color: #4f80e1;
  background: #eef3fd;
}

/* 訂單明細（緊湊版） */
.order-items {
  margin-bottom: 10px;
}

.purchased-items-card .order-items {
  max-height: 360px;
  overflow: auto;
  padding-right: 4px;
}

.purchased-items-card {
  flex: 1;
  min-height: 0;
  max-height: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.purchased-items-card .order-items {
  flex: 1;
  min-height: 0;
  max-height: none;
}

.order-items-empty {
  padding: 14px 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  text-align: center;
  font-size: 12px;
  color: #64748b;
}

.card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 2px solid #f0f0f0;
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.card-title-row .section-title {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
  flex: 1;
}

.order-item {
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
}

.item-main-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.item-name-group {
  display: flex;
  gap: 8px;
}

.item-name-group .form-control {
  flex: 1;
}

.sku-input {
  max-width: 140px;
  flex: 0 0 140px !important;
}

.item-num-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.item-num-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 64px;
}

.item-num-cell:nth-child(2),
.item-num-cell:nth-child(3) {
  flex: 0 0 86px;
  min-width: 86px;
}

.num-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
}

.item-num-cell .form-control {
  padding: 5px 8px;
  height: 34px;
  font-size: 14px;
}

.qty-stepper {
  display: flex;
  align-items: center;
  gap: 4px;
}

.qty-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #d8dbe0;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.qty-btn:hover {
  border-color: #4f80e1;
  color: #2a5bd7;
}

.qty-input {
  width: 64px;
  text-align: center;
}

.subtotal-cell {
  flex: 0 0 auto;
  min-width: 72px;
}

.subtotal-value {
  font-weight: 600;
  font-size: 14px;
  color: #222;
  line-height: 34px;
}

.item-delete-btn {
  align-self: flex-end;
  margin-bottom: 2px;
  height: 34px;
  width: 34px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* 右欄：金額卡片 */
.amount-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 16px;
}

.amount-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.amount-card-header .section-title {
  margin-bottom: 0;
  border-bottom: none;
  padding-bottom: 0;
}

.amount-collapsed {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.amount-collapsed-label {
  font-size: 13px;
  color: #991b1b;
}

.amount-collapsed-value {
  font-size: 18px;
  font-weight: 700;
  color: #dc2626;
}

.amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.amount-row:last-child {
  margin-bottom: 0;
}

.amount-label {
  font-size: 14px;
  color: #555;
  white-space: nowrap;
  flex-shrink: 0;
}

.amount-value {
  font-size: 14px;
  font-weight: 600;
  color: #222;
}

.amount-input-wrap {
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.amount-input {
  width: 120px;
  text-align: right;
  padding: 5px 8px;
  height: 34px;
  font-size: 14px;
}

.total-row .amount-label {
  font-weight: 700;
  font-size: 15px;
  color: #222;
}

.total-input {
  font-weight: 700;
  font-size: 15px;
  color: #1a56d6;
}

.amount-divider {
  height: 1px;
  background: #e8e8e8;
  margin: 8px 0 12px;
}

.mb-8 { margin-bottom: 8px; }
.mt-6 { margin-top: 6px; }
.mt-12 { margin-top: 12px; }

.paid-quick-actions {
  margin-top: 6px;
  display: flex;
  gap: 8px;
}

.change-row {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
}

.change-row.positive {
  background: #ecfdf3;
  color: #166534;
}

.change-row.negative {
  background: #fef2f2;
  color: #991b1b;
}

.change-row.negative strong {
  color: #dc2626;
  font-size: 16px;
}

/* 付款方式（右欄緊湊） */
.payment-method-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.payment-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 7px;
  background: #fff;
  color: #555;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.payment-btn i {
  font-size: 16px;
}

.payment-btn:hover {
  border-color: #4f80e1;
  color: #4f80e1;
  background: #f5f8ff;
}

.payment-btn.active {
  border-color: #4f80e1;
  background: #4f80e1;
  color: #fff;
}

.payment-status-title {
  margin-top: 16px;
}

.points-notice {
  margin-top: 12px;
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 5px;
  background: #fffbea;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 7px 10px;
}

.points-notice i {
  color: #d97706;
  font-size: 14px;
}

/* 右欄按鈕 */
.right-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.right-actions.compact-actions .btn {
  min-height: 34px;
  padding: 6px 10px;
  font-size: 13px;
}

.right-actions.compact-actions {
  flex-direction: row;
}

.right-actions.compact-actions .btn {
  flex: 1;
}

.btn-block {
  width: 100%;
  justify-content: center;
}

/* 點數提示 */
.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
}

.alert-info {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
  color: #0c5460;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

/* 付款方式按鈕 */
.payment-method-group {
  grid-column: 1 / -1;
}

.payment-method-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.payment-method-buttons.is-invalid .payment-btn {
  border-color: #dc3545;
}

.payment-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.payment-btn i {
  font-size: 18px;
}

.payment-btn:hover {
  border-color: #4f80e1;
  color: #4f80e1;
  background: #f5f8ff;
}

.payment-btn.active {
  border-color: #4f80e1;
  background: #4f80e1;
  color: #fff;
}

/* picker 樣式由 ProductPickerModal 元件自身提供 */
.btn-ghost {
  background: none;
  border: 1px solid #d0d0d0;
  color: #555;
}
.btn-ghost:hover { background: #f5f5f5; }
.btn-ghost.active { background: #e8f0fe; border-color: #4f80e1; color: #2a5bd7; }

@media (max-width: 900px) {
  .create-order-container.is-pos-step,
  .create-order-container.is-checkout-step {
    height: auto;
    overflow: visible;
  }

  .create-order-container.is-pos-step .form-container,
  .create-order-container.is-checkout-step .form-container {
    overflow: visible;
    display: block;
  }

  .pos-screen {
    overflow: visible;
  }

  .pos-screen > form {
    display: block;
    overflow: visible;
  }

  .step2-layout {
    grid-template-columns: 1fr;
  }

  .checkout-layout {
    grid-template-columns: 1fr;
  }

  .checkout-step,
  .checkout-form {
    overflow: visible;
  }

  .checkout-left {
    display: block;
  }

  .checkout-left .amount-card {
    max-height: none;
    height: auto;
    overflow: visible;
  }

  .checkout-items-list {
    max-height: 320px;
  }

  .step2-left,
  .step2-left .form-section,
  .quick-select-panel {
    display: block;
  }

  .pos-screen .step2-left .form-section {
    height: auto;
    margin-bottom: 28px;
  }

  .pos-screen .quick-select-panel {
    height: auto;
    min-height: 0;
  }

  .purchased-items-card {
    flex: none;
    max-height: none;
  }

  .step2-right {
    position: static;
  }

  .right-actions {
    flex-direction: row;
  }
}

@media (max-width: 768px) {
  .form-container { padding: 16px; }
  .form-row { grid-template-columns: 1fr; }
  .customer-type-row { flex-direction: column; }
  .step-line { min-width: 30px; }
  .picker-modal { max-height: 90vh; }
  .item-name-group { flex-direction: column; }
  .sku-input { flex: 1 !important; max-width: 100%; }
  .quick-select-header { flex-direction: column; align-items: stretch; }
  .quick-search-input { max-width: none; }
  .quick-products-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); max-height: 260px; }
}

.inventory-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 12px;
  color: #2a7a4b;
  background: #edf8f1;
  border: 1px solid #b5dfca;
  border-radius: 4px;
  padding: 5px 10px;
}

.inventory-hint i {
  font-size: 14px;
  flex-shrink: 0;
}

.inventory-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
  padding: 10px 14px;
  background: #fff1f2;
  border: 1.5px solid #f87171;
  border-left: 4px solid #dc2626;
  border-radius: 6px;
  color: #991b1b;
}

.inventory-alert i {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
  color: #dc2626;
}

.inventory-alert strong {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
}

.inventory-alert p {
  margin: 0;
  font-size: 12px;
  color: #b91c1c;
  line-height: 1.5;
}
</style>

<style>
.page-header-order-create {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}

.sidebar.collapsed ~ .main-content .page-header-order-create {
  left: 64px;
}

@media (max-width: 768px) {
  .page-header-order-create {
    left: 0 !important;
    top: 56px;
  }
}
</style>
